import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  type JSX,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { toast } from "react-hot-toast";
import { useCollaborativeForm } from "../hooks/useCollaborativeForm";
import {
  AssessmentService,
  type AvailableResponse,
  type AvailableResponseItem,
  type SavingsContextItem,
} from "../services/assessmentService";
import {
  type CalculatorLevel,
  CalculatorService,
  type LabWasteSubmissionResponse,
  type FieldData as ServiceFieldData,
  type FormColumn as ServiceFormColumn,
  type SubmissionResults,
} from "../services/calculatorService";
import { ChecklistService } from "../services/checklistService";
import { LabService } from "../services/labService";
import { cx } from "../utils/functions";
import { Alert } from "./Alert";
import { Alert as UiAlert } from "./ui/Alert";
import { DataStorageAssessment } from "./calculators/DataStorageAssessment";
import { DryLabAssessment } from "./calculators/DryLabAssessment";
import type {
  ExtendedFormRow,
  ExtendedFormSchema,
} from "./calculators/formTypes";
import { LabWasteSubmission } from "./calculators/LabWasteSubmission";
import { mapAssessmentPayloadToSubmissionResults } from "./calculators/submissionResults";
import {
  LockSubmitModal,
  type LockSubmitModalRef,
} from "./calculators/LockSubmitModal";
import { AddRowTable, type FieldA11y } from "./calculators/AddRowTable";
import { addRowConfigFor } from "./calculators/addRowCatalogues";
import { HeavyEquipmentGroups } from "./calculators/HeavyEquipmentGroups";
import {
  ECO_OFFICE_COLUMNS,
  isEcoOfficeCategory,
  ecoOfficeMissingColumnsFor,
  isUnusedAmountColumn,
} from "./calculators/ecoOfficeCatalogue";
import {
  EQUIPMENT_TYPE_FIELD_KEY,
  HEAVY_EQUIPMENT_CATEGORIES,
  MIN_EQUIPMENT_TYPES,
  columnIdOf,
  droppedFieldKeysFor,
  equipmentRowNames,
  equipmentTypesCovered,
  fieldKeyOf,
  hasEnoughEquipmentTypes,
  isHeavyEquipmentCategory,
  missingFieldKeysFor,
  rowValuesByFieldKey,
  visibleFieldKeysFor,
} from "./calculators/heavyEquipmentCatalogue";
import {
  WATER_CATEGORIES,
  isSustainableWaterCategory,
  waterColumnIdOf,
  waterDroppedFieldKeysFor,
  waterFieldKeyOf,
  waterMissingFieldKeysFor,
  waterRowValuesByFieldKey,
  waterVisibleFieldKeysFor,
} from "./calculators/sustainableWaterCatalogue";

import { ProcurementTable } from "./calculators/ProcurementAssessment";
import {
  availableProcurementOptions,
  dependentColumnIdsOf,
  MIN_PROCUREMENT_ITEMS,
  PROCUREMENT_CATEGORY,
  parentColumnIdOf,
  procurementItemKey,
  resolveAllowedValues,
} from "./calculators/procurementCatalogue";
import { SubmissionResult } from "./calculators/SubmissionResult";
import {
  CollaborationIndicator,
  UpdateAnimationOverlay,
} from "./CollaborationIndicator";
import { icons } from "./icons";
import { ExampleEntries } from "./lab-details/ExampleEntries";
import { Instructions } from "./lab-details/Instructions";
import { Button } from "./ui/Button";
import { HelpIcon } from "./ui/HelpIcon";
import { Card } from "./ui/Card";

interface Lab {
  id: string;
  type: string;
  name?: string;
  current_certification?: {
    id: number;
    level: string;
    state: string;
  } | null;
}

interface Category {
  category: string;
  description?: string;
  status?: string;
  // Completeness integration
  completeness_percentage?: number;
  completeness_status?: string;
  // Savings-mode fields (populated from /available)
  assessment_type?: string;
  baseline_submission_id?: number | null;
  baseline_submission_state?: string | null;
  baseline_result_available?: boolean;
  savings_submission_id?: number | null;
  savings_submission_state?: string | null;
  savings_result_available?: boolean;
}

interface ValidationErrors {
  [rowId: string]: { [columnId: string]: string };
}

interface FormData {
  [rowId: string]: { [columnId: string]: string };
}

interface DynamicRowMeta {
  id: string;
  sort_order?: number;
}

interface CalculatorProps {
  lab: Lab;
  show?: boolean;
  onClose?: () => void;
  type: "baseline" | "savings";
  previewMode?: boolean;
  selectedTier?: "bronze" | "silver" | "gold";
}

interface FieldData {
  column_id: string | number;
  value: string | number | boolean | null;
  row_id?: string | number;
  dynamic_row_id?: string;
}

const CATEGORY_TO_ICON: Record<string, string | JSX.Element> = {
  "Computer Hardware": icons.computerHardware,
  "Eco Office": icons.ecoOffice,
  "Eco Offices": icons.ecoOffice,
  "Data Storage": icons.eco_database,
  "Cold Storage": icons.coldStorage,
  "Lab Waste": icons.labWaste,
  Transport: icons.transport,
  "Green Chemistry": icons.greenChemistry,
  Procurement: icons.procurement,
  ...Object.fromEntries(WATER_CATEGORIES.map((name) => [name, icons.water])),
  // Keyed off the catalogue's own alias list. Restating the spellings is what
  // left "Heavy Equipment" — the name the backend actually sends — without an
  // icon while every other lookup matched it.
  ...Object.fromEntries(
    HEAVY_EQUIPMENT_CATEGORIES.map((name) => [name, icons.equipment]),
  ),
};

const DEV_PREFILL_SAVINGS_CATEGORIES = new Set([
  "Cold Storage",
  "Computer Hardware",
  "Lab Waste",
]);

const MIN_CHEMICALS = 3;

/** How much of the missing-field list the banner prints before summarising.
 * Capped on both axes: one row with every column missing is as unreadable as
 * twenty rows. */
const MAX_ROWS = 2;
const MAX_COLUMNS = 3;

/** The banner the disabled Submit button points at, since a disabled button is
 * not focusable and can explain itself no other way. */
const INCOMPLETE_FIELDS_ID = "calculator-incomplete-fields";

// Client-approved copy that supersedes the API's `description` for specific
// categories (issue #347). Deliberately narrow — categories absent from this map
// keep whatever the backend returns, so this never silently masks live copy.
const CATEGORY_DESCRIPTION_OVERRIDES: Record<string, string> = {
  "Green Chemistry":
    "Measure the baseline of environmentally damaging chemicals your team may use",
  Transport: "Measure your teams current transport activity to and from work",
};

/**
 * Time-ordered row id: a base36 millisecond timestamp, then random padding.
 *
 * The ids are opaque keys, but sorting them now yields the order the rows were
 * created. That matters because dynamic rows carry no persisted sort order for
 * most assessments, so the only thing keeping a reloaded table in a sensible
 * order is the id itself. Purely random ids reshuffled the table on every visit.
 *
 * Rows saved before this change keep their old random ids and will sort among
 * themselves arbitrarily. Only ordering is affected, never identity or data.
 */
const generateRowId = (): string => {
  const createdAt = Date.now().toString(36).padStart(9, "0");
  const suffix = Math.random().toString(36).substring(2, 6).padEnd(4, "0");
  return `${createdAt}${suffix}`;
};

/** Map a savings assessment submission_state to the category display status. */
/**
 * Translate saved assessment values (from /values endpoint + field_map) to the
 * legacy {rowId: {colId: value}} FormData shape used by the savings After column.
 */
const buildSavingsFormData = (
  values: Array<{ field_key: string; row_key: string | null; value: unknown }>,
  fieldMap: Array<{
    field_key: string;
    legacy_column_id: number;
    legacy_row_id: number | null;
  }>,
): FormData => {
  const mapByKey = Object.fromEntries(fieldMap.map((e) => [e.field_key, e]));
  const result: FormData = {};
  for (const val of values) {
    const entry = mapByKey[val.field_key];
    if (!entry) continue;
    const colId = String(entry.legacy_column_id);
    const rowId =
      val.row_key !== null
        ? val.row_key
        : entry.legacy_row_id !== null
          ? String(entry.legacy_row_id)
          : null;
    if (rowId === null) continue;
    result[rowId] = {
      ...(result[rowId] ?? {}),
      [colId]:
        val.value !== null && val.value !== undefined ? String(val.value) : "",
    };
  }
  return result;
};

const savingsStatusFromState = (state: string | null | undefined): string => {
  if (state === "LOCKED") return "COMPLETED";
  if (state === "NOT_APPLICABLE") return "NOT_APPLICABLE";
  return "NOT_STARTED";
};

/** Map a single /available response item to the Category shape. */
const mapAvailableItemToCategory = (item: AvailableResponseItem): Category => {
  const status = savingsStatusFromState(item.savings_submission_state);
  return {
    category: item.category,
    description: item.description,
    status,
    completeness_status: status,
    assessment_type: item.assessment_type,
    baseline_submission_id: item.baseline_submission_id,
    baseline_submission_state: item.baseline_submission_state,
    baseline_result_available: item.baseline_result_available,
    savings_submission_id: item.savings_submission_id,
    savings_submission_state: item.savings_submission_state,
    savings_result_available: item.savings_result_available,
  };
};

/** Merge a fresh /available response back into the local category list. */
const applySavingsStatuses = (
  available: AvailableResponse,
  prev: Category[],
): Category[] => {
  const itemMap = Object.fromEntries(
    available.items.map((i) => [i.category, i]),
  );
  return prev.map((cat) => {
    const item = itemMap[cat.category];
    if (!item) return cat;
    const status = savingsStatusFromState(item.savings_submission_state);
    return {
      ...cat,
      status,
      completeness_status: status,
      savings_submission_state: item.savings_submission_state,
    };
  });
};

// Transform schema for Eco Office calculator
const transformEcoOfficeSchema = (
  schema: ExtendedFormSchema,
): ExtendedFormSchema => {
  if (!schema.rows || !schema.columns) return schema;

  // Combine E-Waste rows
  const eWasteRows = ["Laptops", "Monitors", "PCs", "Alternative E- Waste"];
  const otherRows = schema.rows.filter(
    (row) => !eWasteRows.includes(row.label),
  );

  // Find the first E-Waste row to use as the combined row
  const firstEWasteRow = schema.rows.find((row) =>
    eWasteRows.includes(row.label),
  );

  let transformedRows = otherRows;
  if (firstEWasteRow) {
    // Create combined E-Waste row
    const combinedEWasteRow = {
      ...firstEWasteRow,
      label: "E-Waste",
      label2: "Combined Electronic Waste",
    };
    transformedRows = [...otherRows, combinedEWasteRow];
  }

  // Rows that should have "Reusable Option in Use?" greyed out
  const greyOutReusableRows = [
    "Paper Notebooks",
    "Journal Print-outs",
    "Printer Cartridges",
    "E-Waste",
  ];

  // Find column ID for "Reusable Option in Use?" if it exists
  const reusableColumnId = schema.columns.find(
    (col) =>
      col.label === "Reusable Option in Use?" || col.label.includes("Reusable"),
  )?.id;

  // Add non-editable columns for specific rows
  transformedRows = transformedRows.map((row) => {
    if (greyOutReusableRows.includes(row.label) && reusableColumnId) {
      return {
        ...row,
        non_editable_columns: [
          ...(row.non_editable_columns || []),
          String(reusableColumnId),
        ],
      };
    }
    // Also handle Electricity/Emissions Factor if they are in the schema
    if (
      row.label.includes("Electricity rate") ||
      row.label.includes("Emissions Factor")
    ) {
      return {
        ...row,
        non_editable_columns: schema.columns?.map((col) => String(col.id)),
      };
    }
    return row;
  });

  return { ...schema, rows: transformedRows };
};

/**
 * The column whose zero value makes the rest of a row moot: quantity, or the
 * bin count for Lab Waste.
 *
 * Resolved per row, not once per form, because a row may not answer the form's
 * usual control column. Eco Office weighs E-waste instead of counting it, so
 * that row's control is its kilograms field. The fallback only applies to forms
 * that already name a control column, so no other assessment gains zero-row
 * behaviour it did not have.
 */

const resolveControlColumnId = (
  columns: ServiceFormColumn[],
  nonEditableColumns: readonly (string | number)[] | undefined,
): string | null => {
  const notApplicable = new Set((nonEditableColumns ?? []).map(String));
  const named = columns.filter((column) => {
    const label = column.label.toLowerCase();
    return (
      label.includes("quantity") ||
      label.includes("no. of bins") ||
      label.includes("number of bins")
    );
  });
  if (named.length === 0) return null;

  const usable = named.find((column) => !notApplicable.has(String(column.id)));
  if (usable) return String(usable.id);

  const fallback = columns.find(
    (column) =>
      column.is_editable &&
      !notApplicable.has(String(column.id)) &&
      (column.constraints?.type === "integer_range" ||
        column.constraints?.type === "decimal_range"),
  );
  return fallback ? String(fallback.id) : null;
};

/**
 * The control column whose zero means "we have none of these", or null when a
 * zero is not a legal answer there.
 *
 * Water and Eco Office serve their quantity with `min_value: 0`, so a zero
 * saves and says the lab does not use that row. Heavy Equipment's quantities
 * start at 1 and 0.25, so a zero fails validation and never reaches the server
 * — skipping a row on it would trust a figure that was never stored.
 */
const zeroMeansNoneColumnId = (
  columns: ServiceFormColumn[],
  nonEditableColumns: readonly (string | number)[] | undefined,
): string | null => {
  const columnId = resolveControlColumnId(columns, nonEditableColumns);
  if (!columnId) return null;
  const min = columns.find((column) => String(column.id) === columnId)
    ?.constraints?.min_value;
  return min == null || min <= 0 ? columnId : null;
};

/**
 * The blank row at the top of a select. Selectable on purpose: it is the only
 * way back out of an answer, and a catalogue row cannot be removed instead.
 */
const SelectPlaceholder = ({ label }: { label: string }): JSX.Element => (
  <option value="">{label}</option>
);

export const Calculator: React.FC<CalculatorProps> = ({
  lab,
  show = false,
  onClose,
  type,
  previewMode = false,
  selectedTier = "bronze",
}): JSX.Element | null => {
  const isDevBuild = import.meta.env.DEV;
  // TODO: We'd need to figure out how to check whether this assessment has been submitted upon re-entry.
  const [lockedAndSubmited, setLockedAndSubmited] = useState<boolean>(false); // To track if the lab is locked and submitted
  const [categories, setCategories] = useState<Category[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  /**
   * Rows the user has actually engaged with. Static rows are "started" the
   * moment they render, so without this every required-empty cell lights up on
   * first paint — an untouched Procurement table is 6 columns x 6 rows of green,
   * which reads as an error state rather than as guidance.
   */
  const [touchedRows, setTouchedRows] = useState<Set<string>>(new Set());
  const [formSchema, setFormSchema] = useState<ExtendedFormSchema | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [savingsData, setSavingsData] = useState<FormData>({}); // For tracking "after" values in savings mode
  const [dynamicRows, setDynamicRows] = useState<DynamicRowMeta[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  // Non-blocking error for completeness fetch
  const [completenessError, setCompletenessError] = useState<string>("");
  const [saveStatus, setSaveStatus] = useState<string>("");
  const queryClient = useQueryClient();
  // For first column validation errors
  const [saveError, setSaveError] = useState<string>("");
  // Track which row has the error
  const [errorRowId, setErrorRowId] = useState<string | null>(null);
  // Prefixes the static table's column-header ids. The cards name a control
  // with <label htmlFor>; a table has no per-cell label, so the control is
  // named from its header instead and the ids must not collide.
  const headerIdPrefix = useId();
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const [submissionResults, setSubmissionResults] =
    useState<SubmissionResults | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Default level for the calculator, right now it's hardcoded to 'bronze'
  const level: CalculatorLevel = selectedTier;

  const lockSubmitModalRef = React.useRef<LockSubmitModalRef>(null);
  // Confirmation modal is triggered directly via ref on click
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  // Savings mode: full context loaded from assessment endpoint
  const [savingsCurrentItem, setSavingsCurrentItem] =
    useState<SavingsContextItem | null>(null);

  const labDetailsQuery = useQuery({
    queryKey: ["calculator:lab-details", lab.id],
    queryFn: () => LabService.getLabDetails(lab.id),
    enabled: type === "savings" && !lab.current_certification?.id,
    staleTime: 30_000,
  });

  const certificationId =
    lab.current_certification?.id ??
    labDetailsQuery.data?.lab.current_certification?.id;

  const checklistGateQuery = useQuery({
    queryKey: ["calculator:checklist-gate", certificationId],
    queryFn: () =>
      ChecklistService.getCurrentChecklist(certificationId!, "lab"),
    enabled: type === "savings" && Boolean(certificationId),
    staleTime: 10_000,
  });

  const savingsRelockBlocked =
    type === "savings" &&
    Boolean(checklistGateQuery.data?.requires_checklist_changes_before_relock);
  const savingsRelockBlockedMessage =
    "Update the current checklist before re-locking reopened savings work.";
  const savingsNotLoadedMessage =
    "Savings assessment could not be loaded, so your changes are not being saved. Please contact support.";

  // Initialize collaborative features for calculator forms
  const collaborativeState = useCollaborativeForm(
    { formData, savingsData },
    {
      formType: type === "baseline" ? "assessment" : "calculator",
      formId: `${lab.id}_${selectedCategory || "calculator"}_${type}`,
      enablePolling: false, // Disable automatic polling to prevent continuous API calls
      pollingInterval: 10000, // 10 seconds when enabled
      showUpdateNotifications: true,
      mergeStrategy: "custom",
      customMerge: (current, update) => {
        const merged = { ...current };

        if (update.formData) {
          merged.formData = { ...current.formData, ...update.formData };
          setFormData(merged.formData);
        }

        if (update.savingsData) {
          merged.savingsData = {
            ...current.savingsData,
            ...update.savingsData,
          };
          setSavingsData(merged.savingsData);
        }

        return merged;
      },
    },
  );

  // Auto-dismiss inline errors after 1.5s
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), 1500);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!completenessError) return;
    const t = setTimeout(() => setCompletenessError(""), 1500);
    return () => clearTimeout(t);
  }, [completenessError]);

  // Get all rows (static + dynamic)
  const getAllRows = useCallback((): ExtendedFormRow[] => {
    const staticRows = formSchema?.rows || [];

    const dynamicLabelPrefix =
      selectedCategory === "Procurement"
        ? "Item"
        : selectedCategory === "Computer Hardware"
          ? "Device"
          : isEcoOfficeCategory(selectedCategory)
            ? "Waste type"
            : isHeavyEquipmentCategory(selectedCategory)
              ? "Equipment"
              : isSustainableWaterCategory(selectedCategory)
                ? "Water use"
              : "Entry";

    // Rows without a sort_order used to compare equal, which left their order
    // down to whatever sequence the API happened to return them in. That made
    // the table reshuffle between visits. Falling back to the row id keeps it
    // stable, and since generateRowId is time-ordered the ids sort into the
    // order the rows were added.
    const sortedDynamicRows = [...dynamicRows].sort((a, b) => {
      const aHasSort = typeof a.sort_order === "number";
      const bHasSort = typeof b.sort_order === "number";

      if (aHasSort && bHasSort) {
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      }

      if (aHasSort && !bHasSort) return 1;
      if (!aHasSort && bHasSort) return -1;
      return a.id.localeCompare(b.id);
    });

    const dynamicRowObjects: ExtendedFormRow[] = sortedDynamicRows.map(
      (row, index: number) => ({
        id: row.id,
        label: `${dynamicLabelPrefix} ${index + 1}`,
        isDynamic: true,
        is_required: false,
      }),
    );

    return [...staticRows, ...dynamicRowObjects];
  }, [formSchema, dynamicRows, selectedCategory]);

  // Generic Validation Logic
  useEffect(() => {
    // Determine which rows to check
    const rowsToCheck = getAllRows();
    if (rowsToCheck.length === 0) {
      // If no rows, maybe valid if it's dynamic?
      // Actually if there are no rows in a dynamic form, can we submit?
      // Existing logic says yes if "hasAnyData" was the check, but now we check validity.
      // If no rows, let's say it's valid (nothing invalid).
      // But wait, if it's dynamic and empty, is that useful?
      // Let's assume valid for now to avoid blocking "Add Row" flows?
      // Actually, if it's empty, there is nothing to submit.
      // But let's check columns.
      setCanSubmit(true);
      return;
    }

    if (!formSchema?.columns) {
      setCanSubmit(false);
      return;
    }

    const validate = (): boolean => {
      // Procurement: at least MIN_PROCUREMENT_ITEMS fully completed rows, and no
      // item claimed twice. Baseline only — savings inherits the baseline's item
      // set, so both were already settled when it was locked.
      if (selectedCategory === PROCUREMENT_CATEGORY && type === "baseline") {
        const required = formSchema.columns.filter(
          (column) => column.is_required && column.is_editable,
        );
        const completeRows = rowsToCheck.filter((row) =>
          required.every((column) => {
            const val = formData[row.id]?.[String(column.id)];
            return typeof val === "string" && val.trim() !== "";
          }),
        );
        if (completeRows.length < MIN_PROCUREMENT_ITEMS) return false;

        // Backstop only. The dropdowns already exclude taken items, but a
        // restored draft could carry duplicates those dropdowns never produced.
        const keys = completeRows
          .map((row) =>
            procurementItemKey(formSchema.columns, formData[row.id] ?? {}),
          )
          .filter((key): key is string => key !== null);
        if (new Set(keys).size !== keys.length) return false;
      }

      // Green Chemistry: require at least MIN_CHEMICALS rows with a chemical selected
      if (selectedCategory === "Green Chemistry") {
        const firstColId = formSchema.columns[0]
          ? String(formSchema.columns[0].id)
          : null;
        if (firstColId) {
          const dataToCheck = type === "savings" ? savingsData : formData;
          const filledRows = rowsToCheck.filter((row) => {
            const val = dataToCheck[row.id]?.[firstColId];
            return val && typeof val === "string" && val.trim() !== "";
          });
          if (filledRows.length < MIN_CHEMICALS) {
            return false;
          }
        }
      }

      // Every per-row requirement, generic and conditional, comes from the
      // one walk the banner also uses, so the two cannot disagree about which
      // categories they know. Heavy Equipment's per-type rules are in there.
      if (missingFieldsByRow().length > 0) return false;

      // The one rule that is not per-row: three different kinds of equipment,
      // not three rows. Baseline only, since savings inherits its row set.
      if (
        isHeavyEquipmentCategory(selectedCategory) &&
        type === "baseline" &&
        !hasEnoughEquipmentTypes(rowsToCheck.map((row) => formData[row.id] ?? {}))
      ) {
        return false;
      }

      return true;
    };

    setCanSubmit(validate());
  }, [
    type,
    formData,
    savingsData,
    formSchema,
    dynamicRows,
    categories,
    getAllRows,
  ]);

  const loadCategories = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError("");
      setCompletenessError("");

      // Savings mode: load categories from the assessment /available endpoint.
      // Scope to the selected tier so e.g. the Bronze tab never pulls the
      // in-flight Silver certification's assessments (Transport/Procurement).
      if (type === "savings") {
        const available = await AssessmentService.getAvailable(lab.id, level);
        setCategories(available.items.map(mapAvailableItemToCategory));
        return;
      }

      const [categoriesRes, statusRes, completenessRes, availableRes] =
        await Promise.allSettled([
          CalculatorService.getCategories(lab.id, level),
          CalculatorService.getCalculatorStatus(lab.id, level),
          CalculatorService.getCalculatorCompleteness(lab.id, level),
          AssessmentService.getAvailable(lab.id, level),
        ]);

      if (
        categoriesRes.status === "rejected" ||
        statusRes.status === "rejected"
      ) {
        // These are critical for the UI
        let msg = "An error occurred while loading categories";
        if (categoriesRes.status === "rejected") {
          const r = categoriesRes.reason as unknown;
          msg = r instanceof Error ? r.message : String(r ?? msg);
        } else if (statusRes.status === "rejected") {
          const r = statusRes.reason as unknown;
          msg = r instanceof Error ? r.message : String(r ?? msg);
        }
        throw new Error(msg);
      }

      const categoryResult = categoriesRes.value;

      const statusResp = statusRes.value;

      const statusMap = Object.fromEntries(
        (statusResp.calculators || []).map(
          (c: { category: string; status: string }) => [c.category, c.status],
        ),
      );

      // Completeness is optional/non-blocking
      let completenessMap: Record<
        string,
        { percentage: number; status: string } | undefined
      > = {};
      if (completenessRes.status === "fulfilled") {
        completenessMap = Object.fromEntries(
          (completenessRes.value.completeness || []).map(
            (c: {
              category: string;
              completeness_percentage: number;
              status: string;
            }) => [
              c.category,
              { percentage: c.completeness_percentage, status: c.status },
            ],
          ),
        );
      } else {
        setCompletenessError("Failed to fetch calculator completeness");
      }

      const availableMap =
        availableRes.status === "fulfilled"
          ? Object.fromEntries(
              availableRes.value.items.map((item) => [item.category, item]),
            )
          : {};

      const mappedCategories: Category[] = categoryResult.map(
        (cat: Category) => ({
          category: cat.category,
          description: cat.description,
          status: cat.status ?? statusMap[cat.category],
          completeness_percentage: completenessMap[cat.category]?.percentage,
          completeness_status: completenessMap[cat.category]?.status,
          assessment_type: availableMap[cat.category]?.assessment_type,
          baseline_submission_id:
            availableMap[cat.category]?.baseline_submission_id,
          baseline_submission_state:
            availableMap[cat.category]?.baseline_submission_state,
          baseline_result_available:
            availableMap[cat.category]?.baseline_result_available,
          savings_submission_id:
            availableMap[cat.category]?.savings_submission_id,
          savings_submission_state:
            availableMap[cat.category]?.savings_submission_state,
          savings_result_available:
            availableMap[cat.category]?.savings_result_available,
        }),
      );

      setCategories(mappedCategories);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred while loading categories";
      // A savings assessment for a tier that hasn't been started yet returns a
      // "no active certification" 404. That's a locked state, not a failure —
      // don't surface it as a red error; the neutral empty state covers it.
      if (type === "savings" && /no active certification/i.test(errorMessage)) {
        setCategories([]);
        setError("");
      } else {
        setError(errorMessage);
      }
      console.error("Error loading categories:", err);
    } finally {
      setLoading(false);
    }
  }, [lab.id, level, type]);

  const refreshStatuses = useCallback(async (): Promise<void> => {
    // Savings mode: derive statuses from assessment submission states,
    // NOT from the baseline calculator bridge (which always returns COMPLETED
    // once baseline is locked, giving false positives for the savings list).
    if (type === "savings") {
      try {
        const available = await AssessmentService.getAvailable(lab.id, level);
        setCategories((prev) => applySavingsStatuses(available, prev));
      } catch (e) {
        console.error("Error refreshing savings status:", e);
      }
      return;
    }
    try {
      const resp = await CalculatorService.getCalculatorStatus(lab.id, level);
      const statusMap = Object.fromEntries(
        (resp.calculators || []).map((c) => [c.category, c.status]),
      );
      setCategories((prev) =>
        prev.map((cat) => ({
          ...cat,
          status: statusMap[cat.category] ?? cat.status,
          completeness_status:
            statusMap[cat.category] ?? cat.completeness_status,
        })),
      );
    } catch (e) {
      console.error("Error refreshing calculator status:", e);
    }
  }, [lab.id, level, type]);

  const locked: boolean = lockedAndSubmited || previewMode;

  // Reset state when the assessment type or level changes to reload categories.
  useEffect(() => {
    setCategories([]);
    setSelectedCategory(null);
    setFormSchema(null);
    setFormData({});
    setSavingsData({});
    setDynamicRows([]);
    setSaveError("");
    setErrorRowId(null);
    setValidationErrors({});
    setTouchedRows(new Set());
    setSubmissionResults(null);
    setLockedAndSubmited(false);
    setSavingsCurrentItem(null);
  }, [level, type]);

  // Load calculator categories when component becomes visible
  useEffect(() => {
    if (show && lab?.id && categories.length === 0) {
      void loadCategories();
    }
  }, [show, lab?.id, categories.length, loadCategories]);

  // Auto-close modal removed to allow viewing completed data

  const loadFormSchema = async (category: string): Promise<void> => {
    try {
      setLoading(true);
      setError("");

      // Savings mode: resolve assessment_type + submission info from the
      // already-loaded categories list, then lazily fetch schema and values.
      let savingsItem: SavingsContextItem | null = null;
      if (type === "savings") {
        const cat = categories.find((c) => c.category === category);
        const assessmentType = cat?.assessment_type;
        if (!assessmentType) {
          setError("Assessment type not found for category: " + category);
          return;
        }
        const submissionId = cat?.savings_submission_id ?? null;

        const [schemaData, valuesData] = await Promise.all([
          AssessmentService.getSchema(lab.id, assessmentType, level),
          submissionId
            ? AssessmentService.getValues(lab.id, assessmentType, submissionId)
            : Promise.resolve(null),
        ]);

        savingsItem = {
          assessment_type: assessmentType,
          submission_id: submissionId,
          submission_state: cat?.savings_submission_state ?? null,
          schema: schemaData.schema,
          field_map: schemaData.field_map,
        };
        setSavingsCurrentItem(savingsItem);

        setSavingsData(
          valuesData
            ? buildSavingsFormData(valuesData.values, schemaData.field_map)
            : {},
        );
      }

      // Load both schema and existing data (baseline formData for "Before" column)
      const [schemaResult, formDataResult] = await Promise.all([
        CalculatorService.getFormSchema(category, lab.id, level),
        CalculatorService.getFormData(category, lab.id, level),
      ]);

      if (schemaResult) {
        // Convert ServiceFormSchema to ExtendedFormSchema first
        let extendedSchema: ExtendedFormSchema = {
          ...schemaResult,
          rows: schemaResult.rows?.map((row) => ({
            id: String(row.id),
            label: row.label,
            label2: row.label2,
            description: row.description,
            is_required: row.is_required,
            non_editable_columns: row.non_editable_columns?.map((id) =>
              String(id),
            ),
          })),
        };

        // Gold's schema carries its own applicability grid, so the legacy
        // transform must not run over it. Bronze still needs it.
        if (isEcoOfficeCategory(category) && level !== "gold") {
          extendedSchema = transformEcoOfficeSchema(extendedSchema);
        }

        // Saved rows the schema does not define are user-added rows.
        const schemaRowIds = new Set(
          (extendedSchema.rows ?? []).map((row) => row.id),
        );
        const savedDynamicRowIds = Object.keys(formDataResult ?? {}).filter(
          (rowId) => !schemaRowIds.has(rowId),
        );

        setFormSchema(extendedSchema);
        setSelectedCategory(category);

        // Check if category is already completed/submitted to restore locked state.
        // In savings mode use the savings submission state from context.
        const currentCategory = categories.find((c) => c.category === category);
        const isCompleted = savingsItem
            ? savingsItem.submission_state === "LOCKED"
            : currentCategory?.status === "COMPLETED" ||
              currentCategory?.status === "SUBMITTED" ||
              currentCategory?.status === "NOT_APPLICABLE";

        if (isCompleted) {
          setLockedAndSubmited(true);
          // Savings mode: no separate results to fetch (submission compute is internal)
          if (!savingsItem) {
            // For completed baselines, read the existing locked result instead of
            // re-posting the submit endpoint, which expects an editable draft.
            if (
              currentCategory?.baseline_result_available &&
              currentCategory.baseline_submission_id
            ) {
              try {
                const resultResponse =
                  await AssessmentService.getSubmissionResult(
                  currentCategory.baseline_submission_id,
                );
                setSubmissionResults(
                  mapAssessmentPayloadToSubmissionResults(
                    category,
                    resultResponse.result,
                  ),
                );
              } catch (e) {
                console.warn("Failed to fetch previous results:", e);
                setSubmissionResults(null);
              }
            } else {
              setSubmissionResults(null);
            }
          } else {
            if (
              savingsItem.submission_state === "LOCKED" &&
              savingsItem.submission_id
            ) {
              try {
                const deltaResponse = await AssessmentService.getDeltaResult(
                  lab.id,
                  savingsItem.assessment_type,
                  level,
                );
                setSubmissionResults(
                  mapAssessmentPayloadToSubmissionResults(
                    category,
                    deltaResponse.delta_result,
                  ),
                );
              } catch (e) {
                console.warn("Failed to fetch previous savings results:", e);
                setSubmissionResults(null);
              }
            } else {
              setSubmissionResults(null);
            }
          }
        } else {
          setLockedAndSubmited(false);
          setSubmissionResults(null);
        }

        const initialData: FormData = {};
        const initialRows: DynamicRowMeta[] = [];
        const existingData = formDataResult || {};

        const getDynamicRowSortOrder = (rowId: string): number | undefined => {
          const rawSortOrder = existingData[rowId]?.sort_order;

          if (
            typeof rawSortOrder === "number" &&
            Number.isFinite(rawSortOrder)
          ) {
            return rawSortOrder;
          }

          if (typeof rawSortOrder === "string") {
            const parsed = Number(rawSortOrder);
            if (Number.isFinite(parsed)) {
              return parsed;
            }
          }

          return undefined;
        };

        const activeColumns = extendedSchema.columns ?? [];
        const activeRows = extendedSchema.rows ?? [];

        /** Copy a row's saved values into edit state, blank where unsaved. */
        const seedRow = (rowId: string): void => {
          const rowData: { [columnId: string]: string } = {};
          activeColumns.forEach((column: ServiceFormColumn) => {
            if (!column.is_editable) return;
            const columnId = String(column.id);
            const savedValue = existingData[rowId]?.[columnId];
            // Booleans become strings so the <select> controls can round-trip.
            rowData[columnId] =
              column.constraints?.type === "boolean" &&
              typeof savedValue === "boolean"
                ? savedValue.toString()
                : savedValue !== undefined && savedValue !== null
                  ? String(savedValue)
                  : "";
          });
          initialData[rowId] = rowData;
        };

        {
          if (activeRows.length === 0 && activeColumns.length > 0) {
            // Add-row table: restore saved rows, or start the user off with one.
            if (savedDynamicRowIds.length === 0) {
              const firstRowId = generateRowId();
              initialRows.push({ id: firstRowId });
              seedRow(firstRowId);
            } else {
              savedDynamicRowIds.forEach((rowId) => {
                initialRows.push({
                  id: rowId,
                  sort_order: getDynamicRowSortOrder(rowId),
                });
                seedRow(rowId);
              });
            }
          } else {
            activeRows.forEach((row) => seedRow(String(row.id)));
          }
        }

        setFormData(initialData);
        // savingsData was already populated above (before the baseline schema load).

        // A row that arrives with saved values has been worked on before, so it
        // keeps flagging what is still missing. Untouched rows start quiet.
        setTouchedRows(
          new Set(
            Object.entries(initialData)
              .filter(([, values]) =>
                Object.values(values).some((value) => value.trim() !== ""),
              )
              .map(([rowId]) => rowId),
          ),
        );
        setDynamicRows(initialRows);
        setSaveError("");
        setErrorRowId(null);
        setValidationErrors({});
      } else {
        setError("Invalid schema response from server");
      }
    } catch (err) {
      setError("An error occurred while loading form schema");
      console.error("Error loading form schema:", err);
    } finally {
      setLoading(false);
    }
  };

  /** Adds a row, optionally with answers already in place, and returns its id. */
  const addDynamicRow = (preset: Record<string, string> = {}): string => {
    const newRowId: string = generateRowId();
    if (!formSchema) return newRowId;

    const newRowData: { [columnId: string]: string } = {};

    formSchema.columns.forEach((column: ServiceFormColumn) => {
      if (column.is_editable) {
        const columnId = String(column.id);
        newRowData[columnId] = preset[columnId] ?? "";
      }
    });

    setDynamicRows((prev: DynamicRowMeta[]) => {
      const maxSortOrder = prev.reduce((max, row) => {
        if (
          typeof row.sort_order === "number" &&
          Number.isFinite(row.sort_order)
        ) {
          return Math.max(max, row.sort_order);
        }
        return max;
      }, 0);

      return [
        ...prev,
        {
          id: newRowId,
          ...(selectedCategory === "Cold Storage"
            ? { sort_order: maxSortOrder + 1 }
            : {}),
        },
      ];
    });
    setFormData((prev: FormData) => ({
      ...prev,
      [newRowId]: newRowData,
    }));
    Object.entries(preset).forEach(([columnId, value]) => {
      void autoSave(newRowId, columnId, value);
    });
    return newRowId;
  };

  const removeDynamicRow = async (rowId: string): Promise<void> => {
    try {
      setSaveStatus("saving");

      // Call API to delete row data from database
      if (!selectedCategory) {
        console.error("No category selected");
        setSaveError("No category selected");
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
        return;
      }

      // Savings mode: delete from the savings assessment submission
      if (type === "savings") {
        if (!savingsCurrentItem || !savingsCurrentItem.submission_id) {
          setSaveError(savingsNotLoadedMessage);
          setSaveStatus("error");
          setTimeout(() => setSaveStatus(""), 2000);
          return;
        }
        const result = await AssessmentService.deleteSavingsRow(
          lab.id,
          savingsCurrentItem.assessment_type,
          savingsCurrentItem.submission_id,
          rowId,
        );
        const deleted = result?.deleted_count ?? 0;
        setDynamicRows((prev) => prev.filter((row) => row.id !== rowId));
        setFormData((prev) => {
          const newData = { ...prev };
          delete newData[rowId];
          return newData;
        });
        setSavingsData((prev) => {
          const newData = { ...prev };
          delete newData[rowId];
          return newData;
        });
        if (errorRowId === rowId) {
          setSaveError("");
          setErrorRowId(null);
        }
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[rowId];
          return newErrors;
        });
        setSaveStatus("saved");
        if (deleted > 0) {
          toast.success("Row deleted successfully");
        } else {
          toast("Row removed (was not saved to database)", {
            icon: "ℹ️",
            duration: 3000,
          });
        }
        setTimeout(() => setSaveStatus(""), 2000);
        return;
      }

      const result = await CalculatorService.deleteRowData(
        selectedCategory,
        lab.id,
        level,
        rowId,
      );

      if (
        result &&
        result.deleted_count !== undefined &&
        result.deleted_count > 0
      ) {
        // Success - row was deleted from backend
        setDynamicRows((prev) => prev.filter((row) => row.id !== rowId));
        setFormData((prev) => {
          const newData = { ...prev };
          delete newData[rowId];
          return newData;
        });
        // Clear error if it was for this row
        if (errorRowId === rowId) {
          setSaveError("");
          setErrorRowId(null);
        }
        // Clear validation errors for this row
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[rowId];
          return newErrors;
        });

        setSaveStatus("saved");
        toast.success("Row deleted successfully");
        setTimeout(() => setSaveStatus(""), 2000);
      } else if (result && result.deleted_count === 0) {
        // Backend returned 0 deleted rows - this means the row doesn't exist in DB
        // This can happen if the row was already deleted or never saved
        // Remove it from frontend state to sync with backend
        console.warn(
          `Row ${rowId} not found in backend (deleted_count: 0). Removing from frontend state.`,
        );

        setDynamicRows((prev) => prev.filter((row) => row.id !== rowId));
        setFormData((prev) => {
          const newData = { ...prev };
          delete newData[rowId];
          return newData;
        });
        // Clear associated errors
        if (errorRowId === rowId) {
          setSaveError("");
          setErrorRowId(null);
        }
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[rowId];
          return newErrors;
        });

        setSaveStatus("saved");
        toast("Row removed (was not saved to database)", {
          icon: "ℹ️",
          duration: 3000,
        });
        setTimeout(() => setSaveStatus(""), 2000);
      } else {
        console.error(
          "Failed to delete row:",
          result?.deleted_count === 0 ? "Row not found" : "Unknown error",
        );
        setSaveError("Failed to delete row. Please try again.");
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
      }
    } catch (err) {
      console.error("Error deleting row:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error occurred while deleting row";
      setSaveError(errorMessage + ". Please try again.");
      setSaveStatus("error");
      toast.error("Failed to delete row");
      setTimeout(() => setSaveStatus(""), 2000);
    }
  };

  const handleDevPrefillSavings = async (): Promise<void> => {
    if (
      type !== "savings" ||
      !formSchema?.columns ||
      !savingsCurrentItem?.submission_id
    ) {
      return;
    }

    Object.values(saveTimeouts.current).forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    saveTimeouts.current = {};

    const staticRowIds = new Set(
      savingsCurrentItem.schema.rows?.map((row) => String(row.id)) ?? [],
    );
    const nextSavingsData: FormData = {};
    const saveOperations: Promise<void>[] = [];

    for (const row of getAllRows()) {
      const rowId = row.id;
      const beforeRow = formData[rowId];
      if (!beforeRow) {
        continue;
      }

      for (const column of formSchema.columns) {
        const columnId = String(column.id);
        if (
          !column.is_editable ||
          row.non_editable_columns?.includes(columnId)
        ) {
          continue;
        }

        const value = beforeRow[columnId] ?? "";
        nextSavingsData[rowId] = {
          ...(nextSavingsData[rowId] ?? {}),
          [columnId]: value,
        };

        const isDynamicRow = !staticRowIds.has(rowId);
        const mapEntry = savingsCurrentItem.field_map.find(
          (entry) =>
            entry.legacy_column_id === Number(columnId) &&
            (isDynamicRow
              ? entry.legacy_row_id === null
              : entry.legacy_row_id === Number(rowId)),
        );

        if (!mapEntry) {
          continue;
        }

        const processedValue =
          typeof value === "string" && value.trim().toLowerCase() === "n/a"
            ? null
            : value;

        saveOperations.push(
          AssessmentService.saveSavingsValue(
            lab.id,
            savingsCurrentItem.assessment_type,
            savingsCurrentItem.submission_id,
            mapEntry.field_key,
            processedValue,
            isDynamicRow ? rowId : undefined,
          ),
        );
      }
    }

    try {
      setSaveStatus("saving");
      setSavingsData(nextSavingsData);
      await Promise.all(saveOperations);
      setSaveStatus("saved");
      toast.success(`Copied baseline values into ${selectedCategory}.`);
    } catch (err) {
      console.error("Failed to prefill savings values:", err);
      setSaveStatus("error");
      toast.error("Failed to prefill savings values");
    } finally {
      setTimeout(() => setSaveStatus(""), 2000);
    }
  };

  // Validate field value against constraints
  const validateFieldValue = (
    value: string,
    constraints: ServiceFormColumn["constraints"] | undefined,
  ): string | null => {
    if (!constraints) return null;

    if (constraints.type === "integer_range") {
      const numValue = parseFloat(value);

      if (
        value !== "" &&
        (!Number.isFinite(numValue) || !Number.isInteger(numValue))
      ) {
        return "Must be a whole number";
      }

      if (Number.isFinite(numValue)) {
        if (
          constraints.min_value != null &&
          numValue < constraints.min_value
        ) {
          return `Must be at least ${constraints.min_value}`;
        }
        if (
          constraints.max_value != null &&
          numValue > constraints.max_value
        ) {
          return `Must be at most ${constraints.max_value}`;
        }
      }
    }

    if (constraints.type === "decimal_range") {
      const numValue = parseFloat(value);

      if (value !== "" && !Number.isFinite(numValue)) {
        return "Must be a number";
      }

      if (Number.isFinite(numValue)) {
        if (
          constraints.min_value != null &&
          numValue < constraints.min_value
        ) {
          return `Must be at least ${constraints.min_value}`;
        }
        if (
          constraints.max_value != null &&
          numValue > constraints.max_value
        ) {
          return `Must be at most ${constraints.max_value}`;
        }
      }
    }

    return null;
  };

  // Check if first column has a value for the given row
  const hasFirstColumnValue = (rowId: string): boolean => {
    if (!formSchema?.columns || formSchema.columns.length === 0) return true;
    const firstColumn = formSchema.columns[0];
    if (!firstColumn?.id) return true;

    const firstColumnId = String(firstColumn.id);
    const dataToCheck = type === "savings" ? savingsData : formData;
    const value = dataToCheck[rowId]?.[firstColumnId];
    return value ? value.trim() !== "" : false;
  };

  /**
   * Started rows that are still short of an answer, and which columns each one
   * is missing.
   *
   * One walk, shared by the submit gate and the banner that explains it. They
   * used to carry a copy each, which is how Sustainable Water ended up blocking
   * submit while the banner stayed silent about why.
   *
   * Conditional requirements are per assessment and cannot be read off
   * `is_required`: Heavy Equipment's depend on the equipment type, Water's on
   * whether Primary Use is "Other", and Water also needs litres above zero
   * rather than at or above the served minimum of zero.
   */
  const missingFieldsByRow = (): {
    label: string;
    missing: string[];
    rowId: string;
    firstColumnId: string;
  }[] => {
    if (!formSchema?.columns) return [];

    const columns = formSchema.columns;
    const requiredColumns = columns.filter((c) => c.is_required && c.is_editable);
    const labelOf = (
      fieldKey: string,
      keyOf: (columnId: string | number) => string,
    ): string =>
      columns.find((c) => keyOf(c.id) === fieldKey)?.label ?? fieldKey;

    const result: ReturnType<typeof missingFieldsByRow> = [];
    const allRows = getAllRows();
    // Heavy Equipment regroups its rows, so a position means nothing on screen.
    // Rows go by the name their group shows instead.
    const equipmentNames = isHeavyEquipmentCategory(selectedCategory)
      ? equipmentRowNames(
          allRows.map((row) => ({
            id: String(row.id),
            label: row.label,
            values: formData[row.id] ?? {},
          })),
        )
      : null;

    allRows.forEach((row, index) => {
      const rowId = row.id;
      // An untouched dynamic row is not incomplete, it is just not started.
      if (row.isDynamic && !hasFirstColumnValue(rowId)) return;

      const rowData =
        (type === "savings" ? savingsData[rowId] : formData[rowId]) ?? {};

      // A zero quantity is the user saying they have none of these, so the
      // rest of the row is deliberately blank.
      const zeroColumnId = zeroMeansNoneColumnId(
        columns,
        row.non_editable_columns,
      );
      if (zeroColumnId) {
        const quantity = rowData[zeroColumnId];
        if (quantity === "0" || quantity === "0.0") return;
      }

      const rowLabel =
        equipmentNames?.get(String(rowId)) ?? `Row ${index + 1}`;

      if (isHeavyEquipmentCategory(selectedCategory)) {
        const keys = missingFieldKeysFor(rowValuesByFieldKey(rowData));
        const [first] = keys;
        if (first) {
          result.push({
            label: rowLabel,
            missing: keys.map((fieldKey) => labelOf(fieldKey, fieldKeyOf)),
            rowId,
            firstColumnId: columnIdOf(first),
          });
        }
        return;
      }

      if (isEcoOfficeCategory(selectedCategory) && level === "gold") {
        const missingLabels = ecoOfficeMissingColumnsFor(
          columns,
          rowData,
          row.non_editable_columns,
        );
        const [firstLabel] = missingLabels;
        if (firstLabel) {
          result.push({
            label: rowLabel,
            missing: missingLabels,
            rowId,
            firstColumnId: String(
              columns.find((c) => c.label === firstLabel)?.id ?? "",
            ),
          });
        }
        return;
      }

      if (isSustainableWaterCategory(selectedCategory)) {
        const keys = waterMissingFieldKeysFor(waterRowValuesByFieldKey(rowData));
        const [first] = keys;
        if (first) {
          result.push({
            label: rowLabel,
            missing: keys.map((fieldKey) => labelOf(fieldKey, waterFieldKeyOf)),
            rowId,
            firstColumnId: waterColumnIdOf(first),
          });
        }
        return;
      }

      const missing: string[] = [];
      let firstColumnId = "";
      for (const column of requiredColumns) {
        if (row.non_editable_columns?.includes(String(column.id))) continue;
        if (selectedCategory === "Transport") {
          const mode = getRowTransportModeValue(rowId);
          if (mode && shouldDisableTransportFieldForMode(column, mode)) continue;
        }
        const value = rowData[String(column.id)];
        if (!value || value.trim() === "") {
          missing.push(column.label);
          firstColumnId ||= String(column.id);
        }
      }

      if (missing.length > 0) {
        // Transport rows are only tellable apart by their mode.
        const mode =
          selectedCategory === "Transport"
            ? getRowTransportModeValue(rowId)
            : null;
        result.push({
          label: mode ? `${rowLabel} (${mode})` : rowLabel,
          missing,
          rowId,
          firstColumnId,
        });
      }
    });

    return result;
  };

  // Get first column label for error message
  const getFirstColumnLabel = (): string => {
    if (!formSchema?.columns || formSchema.columns.length === 0)
      return "first field";
    const firstColumn = formSchema.columns[0];
    return firstColumn?.label ? firstColumn.label.toLowerCase() : "first field";
  };

  // Scroll to the first column field of the error row
  const scrollToFirstColumn = (rowId: string): void => {
    setTimeout(() => {
      const firstColumn = formSchema?.columns?.[0];
      if (!firstColumn) return;
      const firstColumnId = String(firstColumn.id);
      const fieldElement = document.querySelector(
        `[data-row-id="${rowId}"][data-column-id="${firstColumnId}"]`,
      );
      if (fieldElement) {
        fieldElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  /**
   * Takes the user to a field named in the incomplete banner. The table and the
   * cards both render it, so only the copy on screen will take focus.
   */
  const focusField = (rowId: string, columnId: string): void => {
    const field = [
      ...document.querySelectorAll<HTMLElement>(
        `[data-row-id="${CSS.escape(rowId)}"][data-column-id="${CSS.escape(columnId)}"]`,
      ),
    ].find((el) => el.getClientRects().length > 0);
    field?.scrollIntoView({ behavior: "smooth", block: "center" });
    field?.focus({ preventScroll: true });
  };

  const autoSave = async (
    rowId: string,
    columnId: string,
    value: string,
  ): Promise<void> => {
    try {
      // Clear any existing error
      setSaveError("");
      setErrorRowId(null);

      // Only check first column validation for dynamic rows
      const isDynamicRow = dynamicRows.some((row) => row.id === rowId);
      const columnIndex = formSchema?.columns
        ? formSchema.columns.findIndex((col) => String(col.id) === columnId)
        : -1;
      const isFirstColumn = columnIndex === 0;

      if (isDynamicRow && !isFirstColumn && !hasFirstColumnValue(rowId)) {
        // Show error message and focus first column only for dynamic rows
        setSaveError(`Please select a ${getFirstColumnLabel()} first`);
        setErrorRowId(rowId);
        scrollToFirstColumn(rowId);
        return;
      }

      setSaveStatus("saving");

      if (!selectedCategory) {
        console.error("No category selected");
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
        return;
      }

      // Determine if this is a static or dynamic row
      const isStaticRow = formSchema?.rows?.some(
        (row) => String(row.id) === rowId,
      );

      // Convert column_id to number if possible for API compatibility
      const numericColumnId = Number(columnId);
      const fieldData: FieldData = {
        column_id: !isNaN(numericColumnId) ? numericColumnId : columnId,
        value: value,
      };

      if (isStaticRow) {
        const numericRowId = Number(rowId);
        (fieldData as FieldData & { row_id: string | number }).row_id = !isNaN(
          numericRowId,
        )
          ? numericRowId
          : rowId;
      } else {
        (fieldData as FieldData & { dynamic_row_id: string }).dynamic_row_id =
          rowId;
      }

      // For savings mode, translate (columnId, rowId) → field_key via field_map
      // and persist to the savings assessment submission.
      if (type === "savings") {
        if (!savingsCurrentItem || !savingsCurrentItem.submission_id) {
          // Say so on the first keystroke. This used to flash a wordless error
          // icon, so a whole form could be filled before anyone noticed.
          setSaveError(savingsNotLoadedMessage);
          setSaveStatus("error");
          setTimeout(() => setSaveStatus(""), 2000);
          return;
        }

        const isDynamicRow = !savingsCurrentItem.schema.rows?.some(
          (r) => String(r.id) === rowId,
        );
        const colId = parseInt(columnId, 10);
        const rowIdInt = isDynamicRow ? null : parseInt(rowId, 10);
        const mapEntry = savingsCurrentItem.field_map.find(
          (e) =>
            e.legacy_column_id === colId &&
            (isDynamicRow
              ? e.legacy_row_id === null
              : e.legacy_row_id === rowIdInt),
        );

        if (!mapEntry) {
          // No mapping found — skip silently (non-editable column or unmapped)
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus(""), 2000);
          return;
        }

        const processedValue: unknown =
          typeof value === "string" && value.trim().toLowerCase() === "n/a"
            ? null
            : value;

        await AssessmentService.saveSavingsValue(
          lab.id,
          savingsCurrentItem.assessment_type,
          savingsCurrentItem.submission_id,
          mapEntry.field_key,
          processedValue,
          isDynamicRow ? rowId : undefined,
        );

        setSaveStatus("saved");
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          if (newErrors[rowId]) {
            delete newErrors[rowId][columnId];
            if (Object.keys(newErrors[rowId]).length === 0) {
              delete newErrors[rowId];
            }
          }
          return newErrors;
        });
        setTimeout(() => setSaveStatus(""), 2000);
        return;
      }
        // Pass the three parameters separately as expected by the service
        if (!selectedCategory) {
          console.error("No category selected");
          setSaveStatus("error");
          setTimeout(() => setSaveStatus(""), 2000);
          return;
        }

        // Create service-compatible field data
        const processedValue =
          typeof fieldData.value === "string" &&
          fieldData.value.trim().toLowerCase() === "n/a"
            ? null
            : fieldData.value;

        const serviceFieldData: ServiceFieldData = {
          column_id:
            typeof fieldData.column_id === "number"
              ? fieldData.column_id
              : Number(fieldData.column_id),
          value: processedValue,
        };

        if (fieldData.row_id !== undefined) {
          serviceFieldData.row_id =
            typeof fieldData.row_id === "number"
              ? fieldData.row_id
              : Number(fieldData.row_id);
        }

        if (fieldData.dynamic_row_id !== undefined) {
          serviceFieldData.dynamic_row_id = fieldData.dynamic_row_id;
        }

        // Throws on failure, including a save that stored nothing. A clear
        // resolves with no id and is as much a success as a write.
        await CalculatorService.saveFormData(
          selectedCategory,
          lab.id,
          level,
          serviceFieldData,
        );

        setSaveStatus("saved");
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          if (newErrors[rowId]) {
            delete newErrors[rowId][columnId];
            if (Object.keys(newErrors[rowId]).length === 0) {
              delete newErrors[rowId];
            }
          }
          return newErrors;
        });
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (err) {
      setSaveStatus("error");
      console.error("Error saving field:", err);
    }
  };

  const saveTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );

  const handleFieldChange = (
    rowId: string,
    columnId: string,
    value: string,
  ): void => {
    setTouchedRows((prev) =>
      prev.has(rowId) ? prev : new Set(prev).add(rowId),
    );

    // A conditional column's children are no longer valid once it changes: pick
    // Cell Culture / Flask / T25 then switch to Dishes, and without this the row
    // still claims Flask T25. The server would reject the combination at submit,
    // long after the user moved on.
    const dependentIds = dependentColumnIdsOf(
      formSchema?.columns ?? [],
      columnId,
    );
    dependentIds.forEach((dependentId) => {
      const stale = (formData[rowId] ?? {})[dependentId];
      if (stale && stale.trim() !== "") {
        handleFieldChange(rowId, dependentId, "");
      }
    });

    // Sustainable Water is the one assessment with no `value_service` branch,
    // so nothing clears Other Primary Use server-side when the use changes. Left
    // alone the description survives and is echoed back in the result payload.
    if (isSustainableWaterCategory(selectedCategory)) {
      const changedKey = waterFieldKeyOf(columnId);
      if (changedKey) {
        const before = waterRowValuesByFieldKey(formData[rowId] ?? {});
        waterDroppedFieldKeysFor(before, {
          ...before,
          [changedKey]: value,
        }).forEach((droppedKey) => {
          const droppedColumnId = waterColumnIdOf(droppedKey);
          const stale = (formData[rowId] ?? {})[droppedColumnId];
          if (droppedColumnId && stale && stale.trim() !== "") {
            handleFieldChange(rowId, droppedColumnId, "");
          }
        });
      }
    }

    // Same problem, expressed through visibility rather than the schema's
    // conditional values: Heavy Equipment drops questions that stop applying,
    // and the backend soft-deletes their values to match. Local state has to
    // follow, or a question that comes back still shows the old answer while
    // the server holds nothing.
    const changedFieldKey = isHeavyEquipmentCategory(selectedCategory)
      ? fieldKeyOf(columnId)
      : "";
    if (changedFieldKey) {
      const before = rowValuesByFieldKey(formData[rowId] ?? {});
      droppedFieldKeysFor(before, {
        ...before,
        [changedFieldKey]: value,
      }).forEach((droppedKey) => {
        const droppedColumnId = columnIdOf(droppedKey);
        const stale = (formData[rowId] ?? {})[droppedColumnId];
        if (droppedColumnId && stale && stale.trim() !== "") {
          handleFieldChange(rowId, droppedColumnId, "");
        }
      });
    }

    // Find column constraints for validation
    const column = formSchema?.columns?.find(
      (col) => String(col.id) === columnId,
    );
    // The served constraints say everything there is to say about a single
    // value now. Water used to layer an extra greater-than-zero rule on top;
    // it was stale, and because a field error suppresses the autosave it left
    // a displayed zero that never reached the server.
    const validationError = validateFieldValue(value, column?.constraints);

    // Update validation errors
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (validationError) {
        if (!newErrors[rowId]) newErrors[rowId] = {};
        newErrors[rowId][columnId] = validationError;
      } else {
        if (newErrors[rowId]) {
          delete newErrors[rowId][columnId];
          if (Object.keys(newErrors[rowId]).length === 0) {
            delete newErrors[rowId];
          }
        }
      }
      return newErrors;
    });

    // Zero Bin Logic for Lab Waste
    if (
      selectedCategory === "Lab Waste" &&
      column?.label.toLowerCase().includes("no. of bins")
    ) {
      if (value === "0") {
        // Clear other fields in this row
        const otherColumns =
          formSchema?.columns?.filter((c) => String(c.id) !== columnId) || [];

        // Update state to clear fields
        setFormData((prev) => {
          const updatedRow = { ...prev[rowId] };
          otherColumns.forEach((c) => {
            updatedRow[String(c.id)] = "";
          });
          return {
            ...prev,
            [rowId]: {
              ...updatedRow,
              [columnId]: value, // Ensure 0 is kept
            },
          };
        });

        // Trigger auto-save for cleared fields
        otherColumns.forEach((c) => {
          // We use a small delay to avoid race conditions with the main autoSave
          setTimeout(() => {
            autoSave(rowId, String(c.id), "");
          }, 100);
        });
      }
    }

    // Savings mode: update savingsData ("After" column) and trigger debounced
    // auto-save to persist values through the assessment endpoint.
    if (type === "savings") {
      setSavingsData((prev) => {
        const updatedRow = {
          ...prev[rowId],
          [columnId]: value,
        } as { [key: string]: string };
        const storageTypeColId = getStorageTypeColumnId();
        const temperatureColId = getTemperatureColumnId();
        if (
          isColdStorageCategory &&
          storageTypeColId &&
          temperatureColId &&
          String(columnId) === String(storageTypeColId)
        ) {
          const allowed = getAllowedTempsForType(value);
          const currentAfterTempRaw = prev[rowId]?.[temperatureColId];
          const currentAfterTemp =
            currentAfterTempRaw !== undefined && currentAfterTempRaw !== null
              ? String(currentAfterTempRaw)
              : "";
          if (
            currentAfterTemp &&
            allowed.length &&
            !allowed.includes(currentAfterTemp)
          ) {
            updatedRow[temperatureColId] = "";
          }
        }
        return {
          ...prev,
          [rowId]: updatedRow,
        };
      });
      if (!validationError) {
        const timeoutKey = `${rowId}-${columnId}`;
        if (saveTimeouts.current[timeoutKey]) {
          clearTimeout(saveTimeouts.current[timeoutKey]);
        }
        saveTimeouts.current[timeoutKey] = setTimeout(() => {
          autoSave(rowId, columnId, value);
          delete saveTimeouts.current[timeoutKey];
        }, 1000);
      }
    } else {
      // Normal mode - update formData
      const clearedTransportColumns: string[] = [];

      setFormData((prev) => {
        const updatedRow = {
          ...prev[rowId],
          [columnId]: value,
        } as { [key: string]: string };
        const storageTypeColId = getStorageTypeColumnId();
        const temperatureColId = getTemperatureColumnId();
        if (
          isColdStorageCategory &&
          storageTypeColId &&
          temperatureColId &&
          String(columnId) === String(storageTypeColId)
        ) {
          const allowed = getAllowedTempsForType(value);
          const currentTempRaw = prev[rowId]?.[temperatureColId];
          const currentTemp =
            currentTempRaw !== undefined && currentTempRaw !== null
              ? String(currentTempRaw)
              : "";
          if (currentTemp && allowed.length && !allowed.includes(currentTemp)) {
            updatedRow[temperatureColId] = "";
          }
        }

        // Transport: clear values for fields that become disabled under the
        // current Transport Mode, so they don't affect later calculations.
        if (isTransportCategory && formSchema?.columns?.length) {
          const transportModeColId = getTransportModeColumnId();
          let modeForRow = "";

          if (transportModeColId) {
            const modeColIdStr = String(transportModeColId);
            if (modeColIdStr === columnId) {
              // We are editing the Transport Mode itself
              modeForRow = value;
            } else {
              const existingVal =
                updatedRow[modeColIdStr] ?? prev[rowId]?.[modeColIdStr];
              if (
                existingVal !== undefined &&
                existingVal !== null &&
                String(existingVal) !== ""
              ) {
                modeForRow = String(existingVal);
              }
            }
          }

          if (modeForRow && transportModeColId) {
            formSchema.columns.forEach((c) => {
              const cIdStr = String(c.id);
              if (!c.is_editable) return;
              if (cIdStr === String(transportModeColId)) return;

              if (shouldDisableTransportFieldForMode(c, modeForRow)) {
                if (updatedRow[cIdStr]) {
                  updatedRow[cIdStr] = "";
                  clearedTransportColumns.push(cIdStr);
                }
              }
            });
          }
        }

        return {
          ...prev,
          [rowId]: updatedRow,
        };
      });

      // Only auto-save if there's no validation error
      if (!validationError) {
        const timeoutKey = `${rowId}-${columnId}`;
        if (saveTimeouts.current[timeoutKey]) {
          clearTimeout(saveTimeouts.current[timeoutKey]);
        }

        saveTimeouts.current[timeoutKey] = setTimeout(() => {
          autoSave(rowId, columnId, value);
          delete saveTimeouts.current[timeoutKey];
        }, 1000);

        // Also auto-save any Transport fields that we cleared when mode changed
        if (clearedTransportColumns.length > 0) {
          clearedTransportColumns.forEach((clearedId) => {
            const key = `${rowId}-${clearedId}-cleared`;
            if (saveTimeouts.current[key]) {
              clearTimeout(saveTimeouts.current[key]);
            }
            saveTimeouts.current[key] = setTimeout(() => {
              autoSave(rowId, clearedId, "");
              delete saveTimeouts.current[key];
            }, 1000);
          });
        }
      }
    }
  };

  const handleBackToCategories = (): void => {
    setSelectedCategory(null);
    setFormSchema(null);
    setFormData({});
    setSavingsData({});
    setSaveError("");
    setErrorRowId(null);
    setValidationErrors({});
    setTouchedRows(new Set());
    setLockedAndSubmited(false);
    setSubmissionResults(null);
    setSavingsCurrentItem(null);
    void refreshStatuses();
  };

  const refreshCompleteness = async (): Promise<void> => {
    // Savings mode: completeness is tracked via submission state, not the
    // baseline bridge.  Skip the bridge call to avoid stamping wrong data.
    if (type === "savings") return;
    try {
      setCompletenessError("");
      const completenessRes = await CalculatorService.getCalculatorCompleteness(
        lab.id,
        level,
      );

      const completenessMap = Object.fromEntries(
        (completenessRes.completeness || []).map(
          (c: {
            category: string;
            completeness_percentage: number;
            status: string;
          }) => [
            c.category,
            { percentage: c.completeness_percentage, status: c.status },
          ],
        ),
      );

      setCategories((prev) =>
        prev.map((cat) => ({
          ...cat,
          completeness_percentage: completenessMap[cat.category]?.percentage,
          completeness_status: completenessMap[cat.category]?.status,
        })),
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to refresh completeness";
      setCompletenessError(errorMessage);
      console.error("Error refreshing completeness:", err);
    }
  };

  const handleSubmitCalculator = async (): Promise<void> => {
    if (!selectedCategory) return;

    try {
      setIsSubmitting(true);

      if (type === "savings" && savingsRelockBlocked) {
        setError(savingsRelockBlockedMessage);
        toast.error(savingsRelockBlockedMessage);
        return;
      }

      // Savings mode: submit the current assessment via the assessment endpoint
      if (type === "savings") {
        if (!savingsCurrentItem || !savingsCurrentItem.submission_id) {
          setError(savingsNotLoadedMessage);
          toast.error(savingsNotLoadedMessage);
          return;
        }
        const submitResponse = await AssessmentService.submitSavingsAssessment(
          lab.id,
          savingsCurrentItem.assessment_type,
          savingsCurrentItem.submission_id,
        );

        let currentSubmissionState = submitResponse.state;
        let submissionLocked = false;
        let allDone = false;

        try {
          // Refresh category list and trust the server's current submission state
          // before locking the UI for savings mode. Scope to the selected tier.
          const updatedAvailable = await AssessmentService.getAvailable(
            lab.id,
            level,
          );
          const currentCategory = updatedAvailable.items.find(
            (item) => item.category === selectedCategory,
          );

          currentSubmissionState =
            currentCategory?.savings_submission_state ?? submitResponse.state;
          submissionLocked = currentSubmissionState === "LOCKED";

          setCategories((prev) => applySavingsStatuses(updatedAvailable, prev));

          allDone = updatedAvailable.items.every(
            (item) =>
              item.savings_submission_state === "LOCKED" ||
              item.savings_submission_state === "NOT_APPLICABLE",
          );
        } catch (refreshError) {
          console.warn(
            "Failed to refresh savings statuses after submit:",
            refreshError,
          );
        }

        setSavingsCurrentItem((prev) =>
          prev
            ? {
                ...prev,
                submission_state: currentSubmissionState,
              }
            : prev,
        );
        setLockedAndSubmited(submissionLocked);

        let deltaAvailable = false;
        if (submissionLocked) {
          try {
            const deltaResponse = await AssessmentService.getDeltaResult(
              lab.id,
              savingsCurrentItem.assessment_type,
              level,
            );
            setSubmissionResults(
              mapAssessmentPayloadToSubmissionResults(
                selectedCategory,
                deltaResponse.delta_result,
              ),
            );
            deltaAvailable = true;
          } catch (deltaError) {
            console.warn(
              "Savings assessment submitted without delta result:",
              deltaError,
            );
            setSubmissionResults(null);
          }
        } else {
          setSubmissionResults(null);
        }

        if (!submissionLocked) {
          toast.error(
            "Submission did not reach a locked state. Please review the errors and try again.",
          );
        } else if (allDone) {
          toast.success(
            "All savings assessments submitted! Your submission is waiting for audit.",
          );
        } else if (!deltaAvailable) {
          toast.success(
            "Assessment submitted successfully. Savings delta is not available yet.",
          );
        } else {
          toast.success("Assessment submitted successfully!");
        }
        void queryClient.invalidateQueries({
          queryKey: ["lab:stages", lab.id, level],
        });
      } else {
        // Baseline assessment submission (existing logic)
        const results = await CalculatorService.submitCalculator(
          lab.id,
          selectedCategory,
          level,
        );
        setSubmissionResults(results);
        setLockedAndSubmited(true);
        toast.success(
          "Assessment submitted successfully! Review your impact results below.",
        );
        void refreshStatuses();
        void queryClient.invalidateQueries({
          queryKey: ["lab:stages", lab.id, level],
        });
        // Let user view results and navigate manually using 'Back to Categories' button
      }
    } catch (error) {
      console.error("Failed to submit calculator:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Submission failed";
      toast.error(errorMessage);
      // Don't lock form on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLabWasteSubmissionSuccess = (
    response: LabWasteSubmissionResponse,
  ) => {
    // Convert lab waste response to submission results format
    const results: SubmissionResults = {
      cost: response.calculations.annual_cost,
      emissions: response.calculations.annual_emissions,
      waste: response.calculations.annual_waste_kg ?? 0,
      energy: response.calculations.annual_energy_kwh ?? 0,
    };
    setSubmissionResults(results);
    setLockedAndSubmited(true);

    toast.success(
      "✅ Lab Waste assessment locked and submitted! Your annual impact results are shown below.",
    );
    void refreshStatuses();
    void queryClient.invalidateQueries({
      queryKey: ["lab:stages", lab.id, level],
    });
    // Data is now locked - user can view results and navigate manually
  };

  const handleLabWasteSubmissionError = (error: string) => {
    setError(error);
    console.error("Lab waste submission error:", error);
  };

  const getFieldErrorMessage = (
    rowId: string,
    columnId: string,
  ): string | undefined => {
    return validationErrors[rowId]?.[columnId];
  };

  // -------------------------------------------------------------------------
  // Procurement (issue #305)
  // -------------------------------------------------------------------------
  const isProcurement = selectedCategory === PROCUREMENT_CATEGORY;

  /**
   * Values keyed by column id for one row.
   *
   * Savings overlays its "after" values on the baseline rather than replacing
   * them. The identity columns are locked in savings and so carry no after
   * value, but the cascade still has to read them to know which item the row is
   * about — reading `savingsData` alone would make every dropdown think its
   * parent was unset.
   */
  const rowValuesOf = useCallback(
    (rowId: string): Record<string, string> =>
      type === "savings"
        ? { ...(formData[rowId] ?? {}), ...(savingsData[rowId] ?? {}) }
        : (formData[rowId] ?? {}),
    [type, savingsData, formData],
  );

  /**
   * The options a Procurement dropdown should offer for this row: narrowed by
   * the column it depends on, minus any item another row already claims.
   * `null` means the parent is unset, so the control renders disabled.
   *
   * Duplicate filtering is baseline-only. Savings inherits a fixed item set, so
   * there is nothing to deduplicate — and applying it there would strip the
   * row's own already-chosen item out of its list.
   */
  const procurementOptionsFor = (
    row: ExtendedFormRow,
    column: ServiceFormColumn,
  ): string[] | null =>
    availableProcurementOptions(
      column,
      formSchema?.columns ?? [],
      String(row.id),
      rowValuesOf(String(row.id)),
      type === "baseline" ? formData : {},
    );

  /** Rows with every required field filled — what the five-item gate counts. */
  const completeProcurementItemCount = (() => {
    if (!isProcurement || !formSchema?.columns) return 0;
    const required = formSchema.columns.filter(
      (column) => column.is_required && column.is_editable,
    );
    return getAllRows().filter((row) => {
      const values = rowValuesOf(String(row.id));
      return required.every((column) => {
        const value = values[String(column.id)];
        return typeof value === "string" && value.trim() !== "";
      });
    }).length;
  })();

  const isColdStorageCategory = selectedCategory === "Cold Storage";
  const isTemperatureLabel = (label: string): boolean => /temp/i.test(label);
  const isStorageTypeLabel = (label: string): boolean =>
    /cold\s*storage/i.test(label) && /type/i.test(label);
  const getColumnIdBy = (
    predicate: (label: string) => boolean,
  ): string | null => {
    const col = formSchema?.columns?.find((c) => predicate(c.label));
    return col ? String(col.id) : null;
  };
  const getStorageTypeColumnId = (): string | null =>
    getColumnIdBy(isStorageTypeLabel);
  const getTemperatureColumnId = (): string | null =>
    getColumnIdBy(isTemperatureLabel);
  const normalizeStorageTypeValue = (val: string): string => {
    const v = (val || "").toLowerCase();
    if (v.includes("ult")) return "ULT";
    if (v.includes("fridge")) return "Fridge";
    if (v.includes("standard")) return "Standard Freezer";
    if (v.includes("freezer")) return "Standard Freezer";
    return "";
  };
  const getAllowedTempsForType = (val: string): string[] => {
    const key = normalizeStorageTypeValue(val);
    if (key === "ULT") return ["-70", "-75", "-80", "-85"];
    if (key === "Standard Freezer") return ["-15", "-20", "-40"];
    if (key === "Fridge") return ["10", "4"];
    return [];
  };
  const getRowStorageTypeValue = (rowId: string): string => {
    const id = getStorageTypeColumnId();
    if (!id) return "";
    if (type === "savings") {
      const afterVal = savingsData[rowId]?.[id];
      if (
        afterVal !== undefined &&
        afterVal !== null &&
        String(afterVal) !== ""
      ) {
        return String(afterVal);
      }
    }
    const baseVal = formData[rowId]?.[id];
    return baseVal !== undefined && baseVal !== null ? String(baseVal) : "";
  };

  // Helpers for Transport category (Silver baseline)
  const isTransportCategory = selectedCategory === "Transport";
  const isTransportModeLabel = (label: string): boolean =>
    /transport\s*mode/i.test(label);
  const isTripsLabel = (label: string): boolean => {
    const lower = label.toLowerCase();
    return (
      lower.includes("number of trips") ||
      lower.includes("one-way trips") ||
      lower.includes("one way trips") ||
      lower.includes("trips per day") ||
      lower === "trips" ||
      lower.startsWith("trips") ||
      lower.includes("trips")
    );
  };
  const isDaysPerWeekLabel = (label: string): boolean => {
    const lower = label.toLowerCase();
    return (
      lower.includes("days / week") ||
      lower.includes("days per week") ||
      lower.includes("days/week")
    );
  };
  const isOneWayDistanceLabel = (label: string): boolean => {
    const lower = label.toLowerCase();
    return (
      lower.includes("one-way distance") || lower.includes("one way distance")
    );
  };
  const isFuelPowerTypeLabel = (label: string): boolean => {
    const lower = label.toLowerCase();
    return lower.includes("fuel") || lower.includes("power type");
  };
  const isPtTypeLabel = (label: string): boolean => {
    const lower = label.toLowerCase();
    return lower.includes("pt type") || lower.includes("public transport type");
  };
  const isVehicleSizeLabel = (label: string): boolean => {
    const lower = label.toLowerCase();
    return lower.includes("vehicle size");
  };
  const isPeopleInVehicleLabel = (label: string): boolean => {
    const lower = label.toLowerCase();
    return (
      lower.includes("people in vehicle") || lower.includes("people / vehicle")
    );
  };

  const getTransportModeColumnId = (): string | null =>
    getColumnIdBy(isTransportModeLabel);

  const getRowTransportModeValue = (rowId: string): string => {
    const id = getTransportModeColumnId();
    if (!id) return "";
    const baseVal = formData[rowId]?.[id];
    return baseVal !== undefined && baseVal !== null ? String(baseVal) : "";
  };

  const normalizeTransportMode = (val: string): string => {
    const v = (val || "").toLowerCase();
    if (v.includes("public")) return "public_transport";
    if (v.includes("walk") || v.includes("run")) return "walk_or_run";
    if (v.includes("push")) return "push_bike";
    // Combined "E-Bike or Scooter" option (client amendment Feb 18)
    if (v.includes("e-bike or scooter") || v.includes("e bike or scooter"))
      return "e_bike_or_scooter";
    if (v.includes("e-scooter") || v.includes("e scooter")) return "e_scooter";
    if (v.includes("e-bike") || v.includes("e bike")) return "e_bike";
    if (v.includes("car")) return "car";
    return "";
  };

  const shouldDisableTransportFieldForMode = (
    column: ServiceFormColumn,
    rawMode: string,
  ): boolean => {
    const modeKey = normalizeTransportMode(rawMode);
    if (!modeKey) return false;

    const label = column.label || "";

    const isTrips = isTripsLabel(label);
    const isDays = isDaysPerWeekLabel(label);
    const isDistance = isOneWayDistanceLabel(label);
    const isFuelPower = isFuelPowerTypeLabel(label);
    const isPtType = isPtTypeLabel(label);
    const isVehicleSize = isVehicleSizeLabel(label);
    const isPeople = isPeopleInVehicleLabel(label);
    const isTransportModeCol = isTransportModeLabel(label);

    // Never disable the Transport Mode column itself via these rules
    if (isTransportModeCol) return false;

    const isUsageField = isTrips || isDays || isDistance;

    if (modeKey === "public_transport") {
      // Enable: Trips, Days / Week, Distance, PT Type
      // Disable: Fuel / Power Type, Vehicle Size, People in Vehicle
      if (isFuelPower || isVehicleSize || isPeople) return true;
      return false;
    }

    if (modeKey === "car") {
      // Only PT Type disabled
      if (isPtType) return true;
      return false;
    }

    if (
      modeKey === "e_scooter" ||
      modeKey === "e_bike" ||
      modeKey === "e_bike_or_scooter"
    ) {
      // Enable: Trips, Distance, Power Type
      // Disable: PT Type, Vehicle Size, People in Vehicle
      if (isPtType || isVehicleSize || isPeople) return true;
      return false;
    }

    if (modeKey === "push_bike" || modeKey === "walk_or_run") {
      // Enable only usage fields (Trips, Days / Week, Distance)
      // Disable: Fuel / Power Type, PT Type, Vehicle Size, People in Vehicle
      if (!isUsageField) return true;
      return false;
    }

    return false;
  };

  const renderMatrixField = (
    row: ExtendedFormRow,
    column: ServiceFormColumn,
    a11y?: FieldA11y,
  ): JSX.Element => {
    const rowId = String(row.id);
    const columnId = String(column.id);

    // Spread onto whichever control the user actually operates, so the label
    // rendered beside it in AddRowTable has something to point at.
    const a11yProps = {
      id: a11y?.id,
      "aria-describedby": a11y?.describedBy,
      // Only the table sends this. In the cards the visible `<label htmlFor>`
      // is the name, and setting both would have the label read twice.
      "aria-labelledby": a11y?.labelledBy,
    };
    // Savings shows the locked baseline value next to the editable one. Both
    // are the same column, so only the editable one may take the id.
    const beforeA11yProps = { "aria-label": `${column.label}, baseline value` };
    const value = formData[rowId]?.[columnId] ?? "";
    const isEditable =
      column.is_editable &&
      (!row.non_editable_columns ||
        !row.non_editable_columns.some((id) => String(id) === columnId));

    // Check if this field should be greyed out (for Eco Office specific rows)
    const isGreyedOut =
      !isEditable && row.non_editable_columns?.includes(columnId);

    // Check if this field should be disabled for dynamic rows
    const isDynamicRow = dynamicRows.some((row) => row.id === rowId);
    const columnIndex = formSchema?.columns
      ? formSchema.columns.findIndex((col) => String(col.id) === columnId)
      : -1;
    const zeroColumnId = zeroMeansNoneColumnId(
      formSchema?.columns ?? [],
      row.non_editable_columns,
    );

    let isZeroRow = false;
    if (zeroColumnId) {
      const val = formData[rowId]?.[String(zeroColumnId)];
      if (val === "0" || val === "0.0") {
        isZeroRow = true;
      }
    }

    // Transport-specific disabling based on Transport Mode
    let transportDisabled = false;
    if (isTransportCategory) {
      const modeValue = getRowTransportModeValue(rowId);
      if (modeValue) {
        transportDisabled = shouldDisableTransportFieldForMode(
          column,
          modeValue,
        );
      }
    }

    // Eco Office weighs E-waste and counts everything else, so whichever amount
    // column the row does not use reads as not applicable. The backend rejects
    // a row carrying both, but the schema does not say which one applies.
    let ecoOfficeAmountUnused = false;
    if (isEcoOfficeCategory(selectedCategory)) {
      const wasteTypeColumn = formSchema?.columns?.find(
        (c) => c.label === ECO_OFFICE_COLUMNS.wasteType,
      );
      const wasteType = wasteTypeColumn
        ? (rowValuesOf(rowId)[String(wasteTypeColumn.id)] ?? "")
        : "";
      ecoOfficeAmountUnused = isUnusedAmountColumn(column.label, wasteType);
    }

    // Sustainable Water only asks for a written use when Primary Use is
    // "Other". It keeps the shared table, where a column belongs to every row
    // and cannot be dropped for one of them, so the cell is recessed here and
    // `hiddenFor` drops it from the stacked cards.
    let waterUseUnused = false;
    if (isSustainableWaterCategory(selectedCategory)) {
      const rowValues = waterRowValuesByFieldKey(rowValuesOf(rowId));
      waterUseUnused = !waterVisibleFieldKeysFor(rowValues).has(
        waterFieldKeyOf(columnId),
      );
    }

    // Heavy Equipment groups rows by type into tables, so a group's columns are
    // the union across its rows and a column can miss any one of them. The cards
    // drop those via `hiddenFor`; a table has to keep the cell and recess it.
    let equipmentFieldUnused = false;
    if (isHeavyEquipmentCategory(selectedCategory)) {
      const rowValues = rowValuesByFieldKey(rowValuesOf(rowId));
      const equipmentType = rowValues[EQUIPMENT_TYPE_FIELD_KEY] ?? "";
      equipmentFieldUnused =
        equipmentType.trim() !== "" &&
        !visibleFieldKeysFor(equipmentType, rowValues).has(
          fieldKeyOf(column.id),
        );
    }

    /**
     * Cells that can never apply to this row, whatever the user does.
     *
     * Heavy Equipment is in here now that it groups into tables. In the cards
     * `hiddenFor` still drops the column outright, which is the honest answer
     * when 45 of 55 do not apply; in a group's table the cell has to stay and
     * be recessed, because a table cannot drop one row's cell.
     */
    const notApplicableCell =
      transportDisabled ||
      ecoOfficeAmountUnused ||
      waterUseUnused ||
      equipmentFieldUnused;

    // Check if check dependent field disable logic
    // Disable if:
    // 1. It is a dynamic row incomplete first column (existing logic)
    // 2. OR if it is a Zero Row AND this is not the control column itself
    const isControlColumn = zeroColumnId && String(zeroColumnId) === columnId;
    const shouldDisableField =
      (isDynamicRow && columnIndex !== 0 && !hasFirstColumnValue(rowId)) ||
      (isZeroRow && !isControlColumn) ||
      notApplicableCell;

    // Get validation error for this field
    const fieldError = getFieldErrorMessage(rowId, columnId);
    const hasError = !!fieldError;

    if (!isEditable) {
      // Show row labels or greyed out fields
      if (isGreyedOut) {
        // Not a disabled <select>: this path also covers numeric columns, where
        // a dropdown misrepresents the field. A recessed cell with an em-dash
        // says "does not apply" without pretending to be a control.
        //
        // A div is role=generic, which takes no author name, so aria-label here
        // was dropped and the cell announced as an em-dash. Hence sr-only text.
        return (
          <div
            className={cx(
              "p-2 rounded-field text-sm text-center",
              level === "silver"
                ? "field-na-silver"
                : "bg-base-200 text-base-content/50",
            )}
          >
            <span className="sr-only">{column.label}: not applicable</span>
            <span aria-hidden="true">&mdash;</span>
          </div>
        );
      }
      // Show row labels. Silver assessments use the silver tokens here: the
      // gray-50 fallback is lighter than --l0-silver-surface, so prefilled
      // cells read as raised patches on a silver card instead of recessed.
      return (
        <div
          className={cx(
            "p-2 rounded-field text-sm",
            level === "silver"
              ? "field-readonly-silver"
              : "text-gray-500 bg-gray-50",
          )}
        >
          {columnIndex === 0
            ? row.label
            : columnIndex === 1
              ? (row.label2 ?? "N/A")
              : "N/A"}
        </div>
      );
    }

    const constraints = column.constraints;

    /**
     * Options for this cell's dropdown.
     *
     * Resolved once and used by all three render paths below — the baseline
     * field, and savings mode's separate before and after fields. Savings keeps
     * its own copy of the whole control switch, so a resolver applied to only
     * the baseline path would silently offer every item type in the after
     * column.
     *
     * `null` means a conditional column whose parent is still unset.
     *
     * Only the duplicate filtering is Procurement's. Narrowing by
     * `conditional_allowed_values` applies to any assessment whose schema
     * carries them, which keeps this consistent with the child-clearing in
     * `handleFieldChange` — that was already generic, so a non-Procurement
     * cascade used to clear its children without ever narrowing its options.
     *
     * The `length` check matters: `resolveAllowedValues` returns `[]` for a
     * column with no options, where the branch below returns `null` and renders
     * the control disabled. Gating on it keeps every existing assessment on
     * exactly the path it is on today.
     */
    const conditionalOptions: string[] | null = isProcurement
      ? procurementOptionsFor(row, column)
      : constraints?.conditional_allowed_values?.length
        ? resolveAllowedValues(column, rowValuesOf(rowId))
        : (constraints?.allowed_values ?? null);
    const optionsUnavailable = conditionalOptions === null;
    const selectOptions = conditionalOptions ?? [];
    /** Names the column to fill in first, rather than showing an empty list. */
    const parentColumnLabel = (() => {
      if (!optionsUnavailable) return null;
      const parentId = parentColumnIdOf(column);
      const parent = formSchema?.columns?.find(
        (c) => String(c.id) === parentId,
      );
      return parent?.label ?? null;
    })();

    // Add-row tables keep their original rule: a row is started once its first
    // column is filled. Static rows now need an actual edit (or a submission
    // attempt, which marks every row touched) before they flag as incomplete.
    const isRowStarted = row.isDynamic
      ? hasFirstColumnValue(rowId)
      : touchedRows.has(rowId);
    const isEmptyRequired =
      isRowStarted &&
      !shouldDisableField &&
      !locked &&
      column.is_required &&
      (!value || typeof value !== "string" || value.trim() === "");

    // The column does not apply to this row's transport mode. Ranked ahead of the
    // read-only branch because the two need opposite treatments: a locked cell
    // still holds a value worth reading, a not-applicable cell holds none, so
    // this one is deliberately recessed rather than merely inert.
    const notApplicableClass =
      "field-na-silver !opacity-100 cursor-not-allowed";
    // Locked / read-only, or disabled by row-level rules. The !opacity-100 and
    // full-strength text here are deliberate — submitted values must stay legible.
    const readOnlyClass =
      "!bg-gray-100 !text-gray-800 font-medium !border !border-gray-300 shadow-sm !opacity-100 cursor-not-allowed";
    // Required, row started, still empty. Full-opacity green: the previous
    // amber-400/80 + amber-50/40 lost nearly all its chroma over the silver
    // tier's grey surface, which is the wash-out issue #348 reports.
    const emptyRequiredClass =
      "border-green-600 bg-green-50 text-green-900 focus:border-green-700";

    // A not-applicable cell still renders a real <select>/<input>, so without
    // this it keeps inviting the user to "Select" something that can never
    // apply. An em-dash reads as "nothing goes here".
    const selectPlaceholderLabel = notApplicableCell ? "—" : "Select";
    const fieldPlaceholder = notApplicableCell ? "—" : undefined;

    // Both class strings below share one branch order — not-applicable →
    // read-only → error → empty-required → default — so a non-interactive cell
    // never renders an error colour, and a <select> and an <input> in the same
    // state never disagree.
    const baseClassName = `block w-full min-h-11 xl:min-h-0 ${isTransportCategory ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"} border rounded-md shadow-sm focus:ring-1 focus:ring-opacity-50 focus:ring-primary ${
      notApplicableCell
        ? notApplicableClass
        : shouldDisableField || locked
          ? readOnlyClass
          : hasError
            ? "border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50"
            : isEmptyRequired
              ? emptyRequiredClass
              : "border-gray-300 focus:border-primary focus:ring-primary bg-white"
    }`;
    const inputBaseClassName = `input input-bordered ${isTransportCategory ? "input-xs" : "input-sm"} w-full min-h-11 xl:min-h-0 ${
      notApplicableCell
        ? notApplicableClass
        : shouldDisableField || locked
          ? readOnlyClass
          : hasError
            ? "input-error bg-red-50"
            : isEmptyRequired
              ? `border ${emptyRequiredClass}`
              : "bg-white"
    }`;

    const renderField = () => {
      // Cold Storage: render Temperature as conditional dropdown based on storage type
      if (isColdStorageCategory) {
        const tempColId = getTemperatureColumnId();
        const isTempCol = tempColId
          ? String(columnId) === String(tempColId)
          : false;
        if (isTempCol) {
          const allowed = getAllowedTempsForType(getRowStorageTypeValue(rowId));
          const disabledSelect =
            shouldDisableField || locked || allowed.length === 0;
          return (
            <select
              {...a11yProps}
              className={baseClassName}
              value={value}
              onChange={(e) =>
                handleFieldChange(rowId, columnId, e.target.value)
              }
              data-row-id={rowId}
              data-column-id={columnId}
              disabled={disabledSelect}
            >
              <SelectPlaceholder
                label={
                  allowed.length
                    ? selectPlaceholderLabel
                    : "Select storage type first"
                }
              />
              {allowed.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          );
        }
      }

      if (constraints && constraints.type === "boolean") {
        return (
          <select
            {...a11yProps}
            className={baseClassName}
            value={value}
            onChange={(e) => handleFieldChange(rowId, columnId, e.target.value)}
            data-row-id={rowId}
            data-column-id={columnId}
            disabled={shouldDisableField || locked}
          >
            <SelectPlaceholder label={selectPlaceholderLabel} />
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      }

      if (constraints && constraints.type === "exact_values") {
        return (
          <select
            {...a11yProps}
            className={baseClassName}
            value={value}
            onChange={(e) => handleFieldChange(rowId, columnId, e.target.value)}
            data-row-id={rowId}
            data-column-id={columnId}
            disabled={shouldDisableField || locked || optionsUnavailable}
            title={
              parentColumnLabel ? `Select ${parentColumnLabel} first` : undefined
            }
          >
            {/* A table cell cuts the sentence off mid-word, and the column it
                waits on sits in the same row, so the table just says Select. */}
            <SelectPlaceholder
              label={
                parentColumnLabel && !a11y?.labelledBy
                  ? `Select ${parentColumnLabel} first`
                  : selectPlaceholderLabel
              }
            />
            {selectOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      }

      if (constraints && constraints.type === "integer_range") {
        return (
          <input
            {...a11yProps}
            type="number"
            className={inputBaseClassName}
            value={value}
            min={constraints.min_value ?? undefined}
            max={constraints.max_value ?? undefined}
            step={constraints.step_value || 1}
            placeholder={fieldPlaceholder}
            onChange={(e) => handleFieldChange(rowId, columnId, e.target.value)}
            data-row-id={rowId}
            data-column-id={columnId}
            disabled={shouldDisableField || locked}
            readOnly={locked}
            onBlur={(e) => {
              // Additional validation on blur to enforce constraints
              let numValue = parseFloat(e.target.value);
              if (Number.isFinite(numValue)) {
                if (
                  constraints.min_value != null &&
                  numValue < constraints.min_value
                ) {
                  numValue = constraints.min_value;
                }
                if (
                  constraints.max_value != null &&
                  numValue > constraints.max_value
                ) {
                  numValue = constraints.max_value;
                }
                if (numValue !== parseFloat(e.target.value)) {
                  handleFieldChange(rowId, columnId, numValue.toString());
                }
              }
            }}
          />
        );
      }

      if (constraints && constraints.type === "decimal_range") {
        return (
          <input
            {...a11yProps}
            type="number"
            className={inputBaseClassName}
            value={value}
            min={constraints.min_value ?? undefined}
            max={constraints.max_value ?? undefined}
            step={constraints.step_value ?? "any"}
            placeholder={fieldPlaceholder}
            onChange={(e) => handleFieldChange(rowId, columnId, e.target.value)}
            data-row-id={rowId}
            data-column-id={columnId}
            disabled={shouldDisableField || locked}
            readOnly={locked}
            onBlur={(e) => {
              // Clamp to min/max on blur for nicer UX
              let numValue = parseFloat(e.target.value);
              if (Number.isFinite(numValue)) {
                if (
                  constraints.min_value != null &&
                  numValue < constraints.min_value
                ) {
                  numValue = constraints.min_value;
                }
                if (
                  constraints.max_value != null &&
                  numValue > constraints.max_value
                ) {
                  numValue = constraints.max_value;
                }
                if (numValue !== parseFloat(e.target.value)) {
                  handleFieldChange(rowId, columnId, numValue.toString());
                }
              }
            }}
          />
        );
      }

      return (
        <input
          {...a11yProps}
          type="text"
          className={inputBaseClassName}
          value={value}
          placeholder={fieldPlaceholder}
          onChange={(e) => handleFieldChange(rowId, columnId, e.target.value)}
          data-row-id={rowId}
          data-column-id={columnId}
          disabled={shouldDisableField || locked}
          readOnly={locked}
        />
      );
    };

    // For savings mode, show before/after style
    if (type === "savings") {
      const renderBeforeField = () => {
        // Cold Storage: readonly dropdown for temperature (before)
        {
          const tempColId = getTemperatureColumnId();
          const isTempCol = tempColId
            ? String(columnId) === String(tempColId)
            : false;
          if (isColdStorageCategory && isTempCol) {
            const storageTypeColId = getStorageTypeColumnId();
            const baselineType = storageTypeColId
              ? (formData[rowId]?.[storageTypeColId] ?? "")
              : "";
            const allowed = getAllowedTempsForType(String(baselineType));
            return (
              <select
                {...beforeA11yProps}
                className={`select select-bordered select-sm w-full !bg-gray-100 !text-gray-800 font-medium !border !border-gray-300 shadow-sm !opacity-100 cursor-not-allowed`}
                value={value}
                disabled={true}
              >
                <SelectPlaceholder label={selectPlaceholderLabel} />
                {allowed.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            );
          }
        }

        if (constraints && constraints.type === "boolean") {
          return (
            <select
              {...beforeA11yProps}
              className={`select select-bordered select-sm w-full !bg-gray-100 !text-gray-800 font-medium !border !border-gray-300 shadow-sm !opacity-100 cursor-not-allowed`}
              value={value}
              disabled={true}
            >
              <SelectPlaceholder label={selectPlaceholderLabel} />
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          );
        }

        if (constraints && constraints.type === "exact_values") {
          return (
            <select
              {...beforeA11yProps}
              className={`select select-bordered select-sm w-full !bg-gray-100 !text-gray-800 font-medium !border !border-gray-300 shadow-sm !opacity-100 cursor-not-allowed`}
              value={value}
              disabled={true}
            >
              <SelectPlaceholder label={selectPlaceholderLabel} />
              {selectOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        }

        if (constraints && constraints.type === "integer_range") {
          return (
            <input
              {...beforeA11yProps}
              type="number"
              className={`input input-bordered input-sm w-full !bg-gray-100 !text-gray-800 font-medium !border !border-gray-300 shadow-sm !opacity-100 cursor-not-allowed`}
              value={value}
              min={constraints.min_value ?? undefined}
              max={constraints.max_value ?? undefined}
              step={constraints.step_value || 1}
              placeholder={fieldPlaceholder}
              disabled={true}
            />
          );
        }

        return (
          <input
            {...beforeA11yProps}
            type="text"
            className={`input input-bordered input-sm w-full !bg-gray-100 !text-gray-800 font-medium !border !border-gray-300 shadow-sm !opacity-100 cursor-not-allowed`}
            value={value}
            placeholder={fieldPlaceholder}
            disabled={true}
          />
        );
      };

      const renderAfterField = () => {
        const afterValue = savingsData[rowId]?.[columnId] ?? ""; // Get value from savingsData
        // Cold Storage: editable dropdown for temperature (after)
        {
          const tempColId = getTemperatureColumnId();
          const isTempCol = tempColId
            ? String(columnId) === String(tempColId)
            : false;
          if (isColdStorageCategory && isTempCol) {
            const allowed = getAllowedTempsForType(
              getRowStorageTypeValue(rowId),
            );
            const disabledSelect =
              shouldDisableField || locked || allowed.length === 0;
            return (
              <select
                {...a11yProps}
                className={baseClassName}
                value={afterValue}
                onChange={(e) =>
                  handleFieldChange(rowId, columnId, e.target.value)
                }
                data-row-id={rowId}
                data-column-id={columnId}
                disabled={disabledSelect}
              >
                <SelectPlaceholder
                  label={
                    allowed.length
                      ? selectPlaceholderLabel
                      : "Select storage type first"
                  }
                />
                {allowed.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            );
          }
        }

        if (constraints && constraints.type === "boolean") {
          return (
            <select
              {...a11yProps}
              className={baseClassName}
              value={afterValue}
              onChange={(e) =>
                handleFieldChange(rowId, columnId, e.target.value)
              }
              data-row-id={rowId}
              data-column-id={columnId}
              disabled={shouldDisableField || locked}
            >
              <SelectPlaceholder label={selectPlaceholderLabel} />
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          );
        }

        if (constraints && constraints.type === "exact_values") {
          return (
            <select
              {...a11yProps}
              className={baseClassName}
              value={afterValue}
              onChange={(e) =>
                handleFieldChange(rowId, columnId, e.target.value)
              }
              data-row-id={rowId}
              data-column-id={columnId}
              // Matches the baseline field: a conditional column whose parent is
              // unset renders disabled with "Select <parent> first", rather than
              // an enabled dropdown holding nothing.
              disabled={shouldDisableField || locked || optionsUnavailable}
            >
              <SelectPlaceholder label={selectPlaceholderLabel} />
              {selectOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        }

        if (constraints && constraints.type === "integer_range") {
          return (
            <input
              {...a11yProps}
              type="number"
              className={inputBaseClassName}
              value={afterValue}
              min={constraints.min_value ?? undefined}
              max={constraints.max_value ?? undefined}
              step={constraints.step_value || 1}
              placeholder={fieldPlaceholder}
              onChange={(e) =>
                handleFieldChange(rowId, columnId, e.target.value)
              }
              data-row-id={rowId}
              data-column-id={columnId}
              disabled={shouldDisableField || locked}
              readOnly={locked}
              onBlur={(e) => {
                // Additional validation on blur to enforce constraints
                let numValue = parseFloat(e.target.value);
                if (Number.isFinite(numValue)) {
                  if (
                    constraints.min_value != null &&
                    numValue < constraints.min_value
                  ) {
                    numValue = constraints.min_value;
                  }
                  if (
                    constraints.max_value != null &&
                    numValue > constraints.max_value
                  ) {
                    numValue = constraints.max_value;
                  }
                  if (numValue !== parseFloat(e.target.value)) {
                    handleFieldChange(rowId, columnId, numValue.toString());
                  }
                }
              }}
            />
          );
        }

        return (
          <input
            {...a11yProps}
            type="text"
            className={inputBaseClassName}
            value={afterValue}
            placeholder={fieldPlaceholder}
            onChange={(e) => handleFieldChange(rowId, columnId, e.target.value)}
            data-row-id={rowId}
            data-column-id={columnId}
            disabled={shouldDisableField || locked}
            readOnly={locked}
          />
        );
      };

      return (
        <div className="space-y-1">
          <div className="flex gap-2">
            {/* Before (readonly) field */}
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">Before</div>
              {renderBeforeField()}
            </div>
            {/* After (editable) field */}
            <div className="flex-1">
              <div className="text-xs text-green-600 mb-1">After</div>
              {renderAfterField()}
            </div>
          </div>

          {hasError && (
            <div className="absolute top-full left-0 z-10 w-full mt-0.5">
              <div className="text-[10px] text-error leading-tight bg-white/90 px-1.5 py-0.5 rounded shadow-sm border border-error/20 inline-block">
                {fieldError}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative min-w-[120px]">
        {renderField()}
        {hasError && (
          <div className="absolute top-full left-0 z-10 w-full">
            <div className="text-[10px] text-error leading-tight bg-white/90 px-1.5 py-0.5 rounded shadow-sm border border-error/20 mt-0.5 inline-block">
              {fieldError}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleClose = (): void => {
    setSelectedCategory(null);
    setFormSchema(null);
    setFormData({});
    setSavingsData({});
    setCategories([]);
    setError("");
    setSaveError("");
    setErrorRowId(null);
    setValidationErrors({});
    setTouchedRows(new Set());
    if (onClose) {
      onClose();
    }
  };

  // Don't render anything if not shown or no lab
  if (!show || !lab) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="flex flex-col items-center gap-4">
            <span className="loading loading-spinner loading-lg"></span>
            <p>Loading calculator...</p>
          </div>
        </div>
      </div>
    );
  }

  // Do not block UI on errors; show inline alerts instead (handled in views below)

  // Show form if a category is selected
  if (selectedCategory && formSchema) {
    // Gold gets the schema-driven table; bronze keeps its own hardcoded page.
    const isEcoOfficeGold =
      isEcoOfficeCategory(selectedCategory) && level === "gold";
    const hasStaticRows = formSchema?.rows && formSchema.rows.length > 0;
    const allowDynamicRows = !hasStaticRows;
    // Savings inherits the baseline's item set: no adding, no removing, and the
    // five-item minimum was already settled when the baseline was locked.
    //
    // `allowDynamicRows` is in here because a schema may still serve static
    // rows — the pre-redesign backend serves 53 of them — and a catalogue row is
    // not the user's to delete. Without it, Remove renders on every one of them
    // and calls deleteRowData with a row id the endpoint never issued.
    const canEditRowSet = allowDynamicRows && type === "baseline";
    // The add-row table is for assessments that have no rows until the user
    // makes them. Water and Eco Office now serve their rows, so they take the
    // static table Lab Waste uses, which already renders a row's label and
    // label2 and honours its non_editable_columns. Heavy Equipment stays here.
    const addRowConfig = allowDynamicRows
      ? addRowConfigFor(selectedCategory, level)
      : null;
    const canDevPrefillSavings =
      isDevBuild &&
      type === "savings" &&
      !locked &&
      !previewMode &&
      DEV_PREFILL_SAVINGS_CATEGORIES.has(selectedCategory) &&
      Boolean(savingsCurrentItem?.submission_id);
    return (
      <Card variant={level === "silver" ? "silver" : "darkerPrimary"}>
        <div className="card-body">
          {/* Preview Mode Alert - Sticky at top */}
          {previewMode && (
            <div className="alert bg-orange-50 mb-6 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md sticky top-0 z-50 mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-current shrink-0 h-6 w-6 text-orange-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-bold text-orange-900">Preview Mode</h3>
                <div className="text-base font-medium text-orange-900">
                  {type === "savings"
                    ? "The Savings Assessment is in preview mode. Please finish the Checklist first."
                    : "This assessment is in preview mode. Please complete the required prerequisites first."}
                </div>
              </div>
            </div>
          )}

          {(error || completenessError) && (
            <div className="mb-3 space-y-2">
              {error && <Alert type="error" message={error} />}
              {completenessError && (
                <Alert type="error" message={completenessError} />
              )}
            </div>
          )}
          <div className="card-title flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end md:justify-between">
            <h2
              className={cx(
                level === "silver"
                  ? "text-silver-heading font-bold"
                  : "secondary",
              )}
            >
              {formSchema.title || selectedCategory}
            </h2>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              {canDevPrefillSavings && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    void handleDevPrefillSavings();
                  }}
                  disabled={saveStatus === "saving" || isSubmitting}
                >
                  Dev: Copy Before to After
                </Button>
              )}
              {!lockedAndSubmited && !previewMode && (
                <CollaborationIndicator
                  isConnected={collaborativeState.isConnected}
                  hasUpdates={collaborativeState.hasRemoteUpdates}
                  updateCount={collaborativeState.updateCount}
                  lastUpdateBy={
                    collaborativeState.lastUpdate?.updatedBy?.userName
                  }
                  onRefresh={collaborativeState.fetchLatestUpdates}
                />
              )}
              <Button variant="neutral" onClick={handleBackToCategories}>
                ← Back to Categories
              </Button>
            </div>
          </div>
          <Instructions className={"mt-4"} type={type} />
          <div className="flex items-center justify-between mb-6">
            <div>
              {formSchema.description && (
                <p className="text-base-content/70 mt-1">
                  {formSchema.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4 min-h-[2rem]">
              <div className="flex items-center gap-2">
                {saveStatus === "saving" && (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    <span className="text-sm text-base-content/70">
                      Saving...
                    </span>
                  </>
                )}
                {saveStatus === "saved" && (
                  <>
                    <svg
                      className="w-5 h-5 text-success"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-success">Saved</span>
                  </>
                )}
                {saveStatus === "error" && (
                  <>
                    <svg
                      className="w-5 h-5 text-error"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-error">Error saving</span>
                  </>
                )}
                {saveError && (
                  <>
                    <svg
                      className="w-5 h-5 text-warning"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-warning">{saveError}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Form rendering */}
          {formSchema.columns ? (
            <UpdateAnimationOverlay show={collaborativeState.updateAnimation}>
              <div className="space-y-4">
                {isEcoOfficeCategory(selectedCategory) &&
                type === "baseline" &&
                !isEcoOfficeGold ? (
                  <DryLabAssessment
                    labId={lab.id}
                    level={level}
                    initialData={formData}
                    locked={locked}
                    onSubmissionSuccess={(results) => {
                      setLockedAndSubmited(true);
                      setSubmissionResults(results);
                      void refreshStatuses();
                    }}
                    onSubmissionError={(err) => setError(err)}
                  />
                ) : selectedCategory === "Data Storage" &&
                  type === "baseline" ? (
                  <DataStorageAssessment
                    labId={lab.id}
                    level={level}
                    initialData={formData}
                    locked={locked}
                    onSubmissionSuccess={(results) => {
                      setLockedAndSubmited(true);
                      setSubmissionResults(results);
                      void refreshStatuses();
                    }}
                    onSubmissionError={(err) => setError(err)}
                  />
                ) : selectedCategory === "Lab Waste" && type === "baseline" ? (
                  <div className="space-y-6">
                    <div className="flex-1 overflow-x-auto p-2 sm:p-4 lg:p-8 flex flex-col relative">
                      {" "}
                      {/* Restore table for Lab Waste */}
                      <table className="table table-bordered w-full min-w-[1200px]">
                        <thead>
                          <tr>
                            {formSchema.columns.map((column, index) => {
                              const isLastColumn =
                                index >= formSchema.columns.length - 2;
                              return (
                                <th
                                  key={column.id}
                                  id={`${headerIdPrefix}-lw-${column.id}`}
                                  scope="col"
                                  className={cx(
                                    `text-left px-4 py-2 bg-base-200`,
                                    index === 0 &&
                                      "w-[150px] sticky left-0 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]",
                                    index > 0 && "w-[150px]",
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="inline-block leading-tight break-words whitespace-break-spaces">
                                      {column.label}
                                      {column.is_required && (
                                        <span className="ml-2 text-error">
                                          *
                                        </span>
                                      )}
                                    </span>
                                    {column.description && (
                                      <span
                                        className={cx(
                                          "tooltip tooltip-custom cursor-help flex items-center",
                                          isLastColumn
                                            ? "tooltip-left"
                                            : "tooltip-bottom",
                                        )}
                                        data-tip={column.description}
                                      >
                                        <HelpIcon />
                                      </span>
                                    )}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {getAllRows().map((row) => (
                            <tr key={row.id}>
                              {formSchema.columns.map((column, colIndex) => (
                                <td
                                  key={`${row.id}-${column.id}`}
                                  className={cx(
                                    "p-2 align-middle",
                                    colIndex === 0 &&
                                      "sticky left-0 z-10 bg-[#fcf4d6] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]",
                                  )}
                                >
                                  {renderMatrixField(row, column, {
                                    id: `lw-${row.id}-${column.id}`,
                                    labelledBy: `${headerIdPrefix}-lw-${column.id}`,
                                  })}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <LabWasteSubmission
                      labId={lab.id}
                      level={level}
                      formData={formData}
                      formRows={getAllRows() as any}
                      formColumns={formSchema?.columns || []}
                      locked={locked}
                      onSubmissionSuccess={handleLabWasteSubmissionSuccess}
                      onSubmissionError={handleLabWasteSubmissionError}
                    />
                    {lockedAndSubmited && (
                      <div className={"flex flex-row w-full"}>
                        <div>
                          <SubmissionResult
                            labId={lab.id}
                            category={selectedCategory ?? "Lab Waste"}
                            calculatedResults={submissionResults || undefined}
                            mode={type}
                          />
                          <p>
                            You can now go{" "}
                            <button
                              className={"btn btn-sm btn-link p-0"}
                              onClick={handleBackToCategories}
                            >
                              back to other categories
                            </button>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {addRowConfig ? (
                      <div className="pb-4">
                        {(() => {
                          const tableProps = {
                            level,
                            columns: formSchema.columns,
                            rows: getAllRows(),
                            renderField: renderMatrixField,
                            allowRemoveRows: canEditRowSet,
                            onRemoveRow: (rowId: string) =>
                              void removeDynamicRow(rowId),
                            locked,
                            valuesOf: rowValuesOf,
                            config: addRowConfig,
                          };
                          // Heavy Equipment splits into one table per equipment
                          // type; every other add-row assessment is one table.
                          return isHeavyEquipmentCategory(selectedCategory) ? (
                            <HeavyEquipmentGroups
                              {...tableProps}
                              onAddRow={
                                canEditRowSet
                                  ? (equipmentType) =>
                                      addDynamicRow({
                                        [columnIdOf(EQUIPMENT_TYPE_FIELD_KEY)]:
                                          equipmentType,
                                      })
                                  : undefined
                              }
                            />
                          ) : (
                            <AddRowTable {...tableProps} />
                          );
                        })()}
                      </div>
                    ) : isProcurement ? (
                      <div className="pb-4">
                        <ProcurementTable
                          level={level}
                          columns={formSchema.columns}
                          rows={getAllRows()}
                          renderField={renderMatrixField}
                          allowRemoveRows={canEditRowSet}
                          onRemoveRow={(rowId) => void removeDynamicRow(rowId)}
                          locked={locked}
                          completeCount={completeProcurementItemCount}
                          showItemCount={canEditRowSet}
                          // rowValuesOf, not savingsData: the identity columns
                          // are locked in savings and so carry no after value.
                          // Reading savingsData alone would strip Item Type and
                          // Size out of every row heading.
                          valuesOf={rowValuesOf}
                        />
                      </div>
                    ) : (
                    <div className="overflow-x-auto pb-4">
                      {/* Old table (for generic categories) */}
                      <table className="table table-bordered w-full">
                        <thead>
                          <tr>
                            {formSchema.columns.map((column, index) => (
                              <th
                                key={column.id}
                                id={`${headerIdPrefix}-h-${column.id}`}
                                scope="col"
                                className={cx(
                                  "text-left px-2 py-2 bg-base-200 text-xs leading-tight min-w-20 whitespace-normal",
                                  index === 0 && "min-w-28 max-w-36",
                                    index > 0 &&
                                      type === "savings" &&
                                      "max-w-28",
                                    index > 0 &&
                                      type === "baseline" &&
                                      "max-w-32",
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  {!column.description && (
                                    <span className="inline-block leading-tight break-words whitespace-break-spaces">
                                      {column.label}
                                      {column.is_required && (
                                        <span className="ml-2 text-error">
                                          *
                                        </span>
                                      )}
                                    </span>
                                  )}
                                  {column.description && (
                                    <>
                                      {/* The first header used to be pinned
                                          whitespace-nowrap inside a capped
                                          column, which is the same overflow
                                          that made the Procurement headers
                                          overlap — one long label away. */}
                                      <span className="inline-block min-w-0 leading-tight break-words whitespace-break-spaces">
                                        {column.label}
                                        {column.is_required && (
                                          <span className="ml-2 text-error">
                                            *
                                          </span>
                                        )}
                                      </span>
                                      <span
                                        className="tooltip tooltip-bottom tooltip-custom cursor-help flex items-center "
                                        data-tip={
                                            column.label.includes(
                                              "% Seal Frost",
                                            )
                                            ? "How frosted are the freezer door seals (the rubber strip around the edge)?"
                                            : column.description
                                        }
                                      >
                                        <HelpIcon />
                                      </span>
                                    </>
                                  )}
                                </div>
                              </th>
                            ))}
                            {/* Baseline only: savings inherits the row set */}
                            {canEditRowSet && (
                              <th className="w-16 bg-base-200">Delete row</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {getAllRows().map((row) => (
                            <tr key={row.id}>
                              {formSchema.columns.map((column) => (
                                <td
                                  key={`${row.id}-${column.id}`}
                                  className={`${isTransportCategory ? "p-1" : "p-2"} align-middle`}
                                >
                                  {renderMatrixField(row, column, {
                                    id: `f-${row.id}-${column.id}`,
                                    labelledBy: `${headerIdPrefix}-h-${column.id}`,
                                  })}
                                </td>
                              ))}
                              {/* Baseline only: savings inherits the row set */}
                              {canEditRowSet && (
                                <td className="p-2 align-middle">
                                    {row.isDynamic &&
                                      dynamicRows.length > 1 && (
                                    <button
                                      type="button"
                                      disabled={locked}
                                      className="btn btn-sm btn-circle btn-ghost text-error hover:bg-error hover:text-white transition-colors"
                                      onClick={() =>
                                        removeDynamicRow(String(row.id))
                                      }
                                      title="Remove row"
                                    >
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}

                    {lockedAndSubmited && (
                      <div className={"flex flex-row w-full"}>
                        <div>
                          <SubmissionResult
                            labId={lab.id}
                            category={selectedCategory ?? ""}
                            calculatedResults={submissionResults || undefined}
                            mode={type}
                          />
                          <p>
                            You can now go{" "}
                            <button
                              className={"btn btn-sm btn-link p-0"}
                              onClick={handleBackToCategories}
                            >
                              back to other categories
                            </button>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Generic controls (Add Row / Lock & Submit) */}
                    <div className="card-actions flex flex-col gap-2">
                      {savingsRelockBlocked && (
                        <div className="w-full mb-3">
                          <Alert
                            type="warning"
                            message={savingsRelockBlockedMessage}
                          />
                        </div>
                      )}

                      {selectedCategory === "Green Chemistry" &&
                        !canSubmit &&
                        (() => {
                        const firstColId = formSchema?.columns?.[0]
                          ? String(formSchema.columns[0].id)
                          : null;
                          const dataToCheck =
                            type === "savings" ? savingsData : formData;
                        const filledCount = firstColId
                          ? getAllRows().filter((row) => {
                              const val = dataToCheck[row.id]?.[firstColId];
                                return (
                                  val &&
                                  typeof val === "string" &&
                                  val.trim() !== ""
                                );
                            }).length
                          : 0;
                        return filledCount < MIN_CHEMICALS ? (
                          <div className="w-full">
                            <Alert
                              type="warning"
                              message={`Please add at least ${MIN_CHEMICALS} chemicals (${filledCount} of ${MIN_CHEMICALS} added). Select a chemical in each row to continue.`}
                            />
                          </div>
                        ) : null;
                      })()}

                      {!canSubmit &&
                        !locked &&
                        (() => {
                        if (!formSchema?.columns) return null;
                          const firstColId = formSchema.columns[0]
                            ? String(formSchema.columns[0].id)
                            : null;
                          const dataToCheck =
                            type === "savings" ? savingsData : formData;

                          if (
                            selectedCategory === "Green Chemistry" &&
                            firstColId
                          ) {
                          const filledCount = getAllRows().filter((row) => {
                            const val = dataToCheck[row.id]?.[firstColId];
                              return (
                                val &&
                                typeof val === "string" &&
                                val.trim() !== ""
                              );
                          }).length;
                          if (filledCount < MIN_CHEMICALS) return null; // already displayed Green Chemistry count alert
                        }

                        const missingByRow = missingFieldsByRow();

                        // Every row can be complete and submit still blocked,
                        // because the lab needs three different kinds of
                        // equipment. Without this the banner stays hidden and
                        // Submit is dead with nothing explaining why.
                        const isEquipment =
                          isHeavyEquipmentCategory(selectedCategory);
                        const equipmentRows = isEquipment
                          ? getAllRows().map((row) => dataToCheck[row.id] ?? {})
                          : [];
                        const typesCovered = isEquipment
                          ? equipmentTypesCovered(equipmentRows).size
                          : 0;
                        const typesShort =
                          isEquipment &&
                          type === "baseline" &&
                          !hasEnoughEquipmentTypes(equipmentRows);

                        if (missingByRow.length === 0 && !typesShort)
                          return null;

                          // Both lists are capped. Capping rows alone left a
                          // single row free to print every missing column as
                          // one unreadable sentence.
                          const shownRows = missingByRow.slice(0, MAX_ROWS);
                          const moreRows =
                            missingByRow.length - shownRows.length;
                        return (
                            <div
                              id={INCOMPLETE_FIELDS_ID}
                              className="w-full mt-1 mb-1"
                            >
                              <UiAlert variant="warning">
                                <div>
                                  <p className="font-medium">
                                    Complete these to enable submission
                                  </p>
                                  <ul className="mt-1 list-disc pl-5 text-sm">
                                    {shownRows.map((entry) => {
                                      const shownCols = entry.missing.slice(
                                        0,
                                        MAX_COLUMNS,
                                      );
                                      const moreCols =
                                        entry.missing.length - shownCols.length;
                                      return (
                                        <li key={entry.rowId}>
                                          <button
                                            type="button"
                                            className="link font-medium"
                                            onClick={() =>
                                              focusField(
                                                entry.rowId,
                                                entry.firstColumnId,
                                              )
                                            }
                                          >
                                            {entry.label}
                                          </button>
                                          {`: ${shownCols.join(", ")}`}
                                          {moreCols > 0 &&
                                            ` and ${moreCols} more`}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                  {moreRows > 0 && (
                                    <p className="mt-1 text-sm">
                                      {`and ${moreRows} more ${moreRows === 1 ? "row" : "rows"}`}
                                    </p>
                                  )}
                                  {typesShort && (
                                    <p className="mt-1 text-sm">
                                      {`Add at least ${MIN_EQUIPMENT_TYPES} different kinds of equipment. You have ${typesCovered} so far.`}
                                    </p>
                                  )}
                                </div>
                              </UiAlert>
                          </div>
                        );
                      })()}

                      <div className="flex justify-between w-full">
                      {/* Heavy Equipment adds rows by type, inside its groups. */}
                      {canEditRowSet &&
                        !isHeavyEquipmentCategory(selectedCategory) && (
                        <Button
                          variant="secondary"
                          type="button"
                          disabled={locked}
                          onClick={() => addDynamicRow()}
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          {addRowConfig?.addLabel ?? "Add Row"}
                        </Button>
                      )}

                      <Button
                        variant="primary"
                        className="ml-auto"
                          // A disabled button is not focusable, so this is the
                          // only route from "Submit is dead" to the reason why.
                          aria-describedby={
                            !canSubmit && !locked
                              ? INCOMPLETE_FIELDS_ID
                              : undefined
                          }
                        disabled={
                            locked ||
                            !canSubmit ||
                            isSubmitting ||
                            savingsRelockBlocked
                        }
                        onClick={async () => {
                          // Submitting is the moment the required-empty
                          // highlight is genuinely useful, so surface it across
                          // every row rather than only the ones already edited.
                          setTouchedRows(
                              new Set(
                                getAllRows().map((row) => String(row.id)),
                              ),
                          );
                          await refreshCompleteness();
                          lockSubmitModalRef.current?.showModal();
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="loading loading-spinner loading-sm mr-2"></span>
                            Submitting...
                          </>
                        ) : (
                          "Submit Assessment"
                        )}
                      </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </UpdateAnimationOverlay>
          ) : (
            <div className="text-center py-12">
              <p className="text-base-content/70">
                No form schema available for this category.
              </p>
            </div>
          )}

          <ExampleEntries category={selectedCategory} level={level} />
        </div>

        {/* Lock & Submit Confirmation Modal */}
        <LockSubmitModal
          ref={lockSubmitModalRef}
          onConfirm={handleSubmitCalculator}
          onCancel={() => {
            // Modal will close automatically
          }}
          loading={isSubmitting}
        />
      </Card>
    );
  }

  // Add close button in the categories view
  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium uppercase tracking-wide ${
              level === "bronze"
                ? "bg-amber-600 text-white"
                : level === "silver"
                  ? "badge-silver"
                  : level === "gold"
                    ? "bg-yellow-500 text-black"
                    : "bg-gray-300 text-gray-700"
            }`}
          >
            {level}
          </span>
          <div>
            {type === "baseline" && (
              <h2 className="text-2xl font-bold">Baseline Assessment</h2>
            )}
            {type === "savings" && (
              <h2 className="text-2xl font-bold">Savings Assessment</h2>
            )}
          </div>
        </div>
        {onClose && (
          <button
            className="btn btn-ghost btn-sm btn-circle hover:bg-red-50 hover:text-red-600 transition-colors"
            onClick={handleClose}
            title="Close Calculator"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="mb-6">
        <p className="text-base-content/70">
          Select a {level} category to begin your assessment for your {lab.type}{" "}
          lab.
        </p>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12">
          {error && (
            <div className="mb-6 max-w-2xl mx-auto text-left">
              <Alert type="error" message={error} />
            </div>
          )}
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">
            No Calculator Categories Available
          </h3>
          <p className="text-base-content/70">
            No calculator categories are available for {lab.type} labs at the{" "}
            {level} certification level.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {(error || completenessError) && (
            <div className="md:col-span-2 lg:col-span-3">
              <div className="mb-2 space-y-2">
                {error && <Alert type="error" message={error} />}
                {completenessError && (
                  <Alert type="error" message={completenessError} />
                )}
              </div>
            </div>
          )}
          {categories.map((category: Category) => {
            const description =
              CATEGORY_DESCRIPTION_OVERRIDES[category.category] ??
              category.description;
            const iconSrc = CATEGORY_TO_ICON[category.category];
            const icon =
              typeof iconSrc === "string" ? (
                <img alt="Icon" src={iconSrc} />
              ) : iconSrc ? (
                iconSrc
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16a2 2 0 002 2z"
                  />
                </svg>
              );

            return (
              <div
                key={category.category}
                className={`card shadow-sm hover:shadow-md transition-all cursor-pointer border-2 border-transparent ${
                  level === "bronze"
                    ? "bg-gradient-to-br from-amber-50 to-amber-100 hover:border-amber-200"
                    : level === "silver"
                      ? "card-silver card-silver-hover"
                      : level === "gold"
                        ? "bg-gradient-to-br from-yellow-50 to-yellow-100 hover:border-yellow-200"
                        : "bg-gradient-to-br from-gray-50 to-gray-100 hover:border-gray-200"
                }`}
                onClick={() => loadFormSchema(category.category)}
              >
                <div className="card-body p-4">
                  <h3
                    className={`card-title mb-2 text-lg ${
                      level === "bronze"
                        ? "text-amber-800"
                        : level === "silver"
                          ? "text-silver-body"
                          : level === "gold"
                            ? "text-yellow-800"
                            : "text-gray-800"
                    }`}
                  >
                    <div
                      className={`w-6 h-6 ${level === "bronze" ? "text-heading" : level === "silver" ? "text-silver-heading" : level === "gold" ? "text-heading" : "text-gray-800"}`}
                    >
                      {icon}
                    </div>
                    {category.category}
                  </h3>

                  {description && (
                    <p
                      className={`text-sm ${
                        level === "bronze"
                          ? "text-amber-600"
                          : level === "silver"
                            ? "text-silver-body"
                            : level === "gold"
                              ? "text-yellow-700"
                              : "text-gray-600"
                      }`}
                    >
                      {description}
                    </p>
                  )}

                  {(category.completeness_status ||
                    typeof category.completeness_percentage === "number") && (
                    <div className="flex items-center gap-2 mt-1">
                      {category.completeness_status &&
                        category.completeness_status !== "IN_PROGRESS" && (
                          <span
                            className={`badge p-3 badge-xs ${
                              category.completeness_status === "NOT_APPLICABLE"
                                ? "badge-info"
                                : category.completeness_status ===
                                      "COMPLETED" ||
                                    category.completeness_status === "COMPLETE"
                                  ? "badge-success"
                                  : "badge-ghost"
                            }`}
                            title={`Status: ${category.completeness_status}`}
                          >
                            {category.completeness_status
                              .replace("_", " ")
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                        )}
                      {/* {typeof category.completeness_percentage === 'number' && (
                        <span className="text-[10px] font-semibold text-base-content">
                          {Math.round(category.completeness_percentage)}%
                        </span>
                      )} */}
                    </div>
                  )}

                  <div className="card-actions justify-end mt-2">
                    <span
                      className={`text-xs font-medium ${
                        level === "bronze"
                          ? "text-amber-500"
                          : level === "silver"
                            ? "text-silver-body"
                            : level === "gold"
                              ? "text-yellow-600"
                              : "text-gray-500"
                      }`}
                    >
                      Click to start →
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {/* Signage for the not-yet-released Procurement assessment. Gated on the
              absence of the real category rather than on a release flag, so it
              retires itself the moment the backend starts serving Procurement.
              Gating it on baseline_progression_enabled instead would be wrong in
              two directions: the tile would vanish while Procurement was still
              unbuilt, and it could sit alongside the real card once it shipped. */}
          {type === "baseline" &&
            level === "silver" &&
            !categories.some((c) => c.category === "Procurement") && (
            <div
              className="card card-silver border-2 border-dashed"
              aria-disabled="true"
            >
              {/* Issue #347 is explicit that no supporting or call-to-action copy
                  may appear on this tile — icon and label only. */}
              <div className="card-body p-4">
                <h3 className="card-title mb-2 text-lg text-silver-body">
                  <div className="w-6 h-6 text-silver-heading">
                    {icons.procurement}
                  </div>
                  Purchasing
                </h3>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
